/**
 * Dynamic LWC Renderer - Advanced Version
 *
 * This component dynamically loads and renders original LWC components
 * from the backend, including their HTML templates, CSS styles, and JavaScript controllers.
 *
 * It provides a generic way to display any LWC component without needing to
 * manually create React equivalents.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../config/config';
import { callApex } from './script-adapter';

const API_BASE = config.api.baseUrl;

/**
 * Calculate bounding box for map markers
 */
function calculateBBox(markers, center) {
    if (!markers || markers.length === 0) {
        // Default bbox around center (about 5km radius)
        const offset = 0.05;
        return {
            north: center.Latitude + offset,
            south: center.Latitude - offset,
            east: center.Longitude + offset,
            west: center.Longitude - offset
        };
    }

    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;

    markers.forEach(m => {
        const lat = parseFloat(m.location?.Latitude || m.Latitude);
        const lng = parseFloat(m.location?.Longitude || m.Longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
        }
    });

    // Add padding (about 10%)
    const latPadding = (maxLat - minLat) * 0.15 || 0.02;
    const lngPadding = (maxLng - minLng) * 0.15 || 0.02;

    return {
        north: maxLat + latPadding,
        south: minLat - latPadding,
        east: maxLng + lngPadding,
        west: minLng - lngPadding
    };
}

/**
 * Resolve a binding path in the state object
 * Supports: simple props, nested props (a.b.c), array access
 */
function resolveBinding(state, bindPath) {
    if (!bindPath) return undefined;

    // Handle direct value (not a binding)
    if (!bindPath.includes('.') && !bindPath.includes('[')) {
        return state[bindPath];
    }

    // Handle nested paths like "item.Name" or "item.account.Name"
    const parts = bindPath.split('.');
    let value = state;

    for (const part of parts) {
        if (value === undefined || value === null) return undefined;

        // Handle array access like "items[0]"
        const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
        if (arrayMatch) {
            value = value[arrayMatch[1]];
            if (Array.isArray(value)) {
                value = value[parseInt(arrayMatch[2])];
            }
        } else {
            value = value[part];
        }
    }

    return value;
}

/**
 * Parse LWC HTML template and convert to React JSX
 */
function parseLWCTemplate(html, componentState, handlers, componentName) {
    if (!html) return null;

    // Remove outer <template> wrapper
    let cleanHtml = html.trim();
    if (cleanHtml.startsWith('<template>') || cleanHtml.match(/^<template\s/)) {
        cleanHtml = cleanHtml.replace(/^<template[^>]*>/, '').replace(/<\/template>\s*$/, '');
    }

    // IMPORTANT: Replace <template> with <lwc-template> to preserve content
    // Browser's innerHTML moves <template> content to .content DocumentFragment
    cleanHtml = cleanHtml.replace(/<template\s/g, '<lwc-template ');
    cleanHtml = cleanHtml.replace(/<template>/g, '<lwc-template>');
    cleanHtml = cleanHtml.replace(/<\/template>/g, '</lwc-template>');

    // IMPORTANT: Also replace table elements to prevent browser from applying
    // table-specific parsing rules that would break our <lwc-template> inside <tbody>
    cleanHtml = cleanHtml.replace(/<table\b/gi, '<lwc-table');
    cleanHtml = cleanHtml.replace(/<\/table>/gi, '</lwc-table>');
    cleanHtml = cleanHtml.replace(/<thead\b/gi, '<lwc-thead');
    cleanHtml = cleanHtml.replace(/<\/thead>/gi, '</lwc-thead>');
    cleanHtml = cleanHtml.replace(/<tbody\b/gi, '<lwc-tbody');
    cleanHtml = cleanHtml.replace(/<\/tbody>/gi, '</lwc-tbody>');
    cleanHtml = cleanHtml.replace(/<tr\b/gi, '<lwc-tr');
    cleanHtml = cleanHtml.replace(/<\/tr>/gi, '</lwc-tr>');
    cleanHtml = cleanHtml.replace(/<td\b/gi, '<lwc-td');
    cleanHtml = cleanHtml.replace(/<\/td>/gi, '</lwc-td>');
    cleanHtml = cleanHtml.replace(/<th\b/gi, '<lwc-th');
    cleanHtml = cleanHtml.replace(/<\/th>/gi, '</lwc-th>');

    // Create a container
    const container = document.createElement('div');
    container.innerHTML = cleanHtml;

    // Debug: Log the parsed HTML to see structure (enable if needed)
    // console.log('[LWC Template] Parsed HTML container:', container.innerHTML.substring(0, 500));

    // Process the template
    const result = processNode(container, componentState, handlers, 0, componentName);
    return result;
}

/**
 * Process a DOM node and convert to React element
 */
function processNode(node, state, handlers, key = 0, componentName = '') {
    if (node.nodeType === Node.TEXT_NODE) {
        let text = node.textContent;
        // Replace {property} and {property.subprop} with state values
        text = text.replace(/\{([\w.[\]]+)\}/g, (match, prop) => {
            const val = resolveBinding(state, prop);
            if (val === undefined || val === null) return '';
            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
        });
        return text.trim() ? text : null;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const tagName = node.tagName.toLowerCase();

    // Handle template tags with directives (lwc-template is our renamed version)
    if (tagName === 'template' || tagName === 'lwc-template') {
        const ifTrue = node.getAttribute('if:true');
        const ifFalse = node.getAttribute('if:false');
        const lwcIf = node.getAttribute('lwc:if');  // New LWC syntax
        const lwcElseIf = node.getAttribute('lwc:elseif');  // New LWC syntax
        const lwcElse = node.hasAttribute('lwc:else');  // New LWC syntax
        const forEach = node.getAttribute('for:each');

        // Handle lwc:if (new syntax)
        if (lwcIf) {
            const prop = lwcIf.replace(/^\{|\}$/g, '');
            const val = resolveBinding(state, prop);
            if (!val) return null;
        }

        // Handle lwc:elseif (treated as lwc:if for now)
        if (lwcElseIf) {
            const prop = lwcElseIf.replace(/^\{|\}$/g, '');
            const val = resolveBinding(state, prop);
            if (!val) return null;
        }

        // Handle if:true
        if (ifTrue) {
            const prop = ifTrue.replace(/^\{|\}$/g, '');
            const val = resolveBinding(state, prop);
            if (prop === 'hasRoutes' || prop === 'loading') {
                console.log(`[LWC Template] if:true={${prop}} = ${val}, children count:`, node.childNodes.length);
            }
            if (!val) return null;
        }

        // Handle if:false
        if (ifFalse) {
            const prop = ifFalse.replace(/^\{|\}$/g, '');
            const val = resolveBinding(state, prop);
            if (prop === 'hasRoutes' || prop === 'loading') {
                console.log(`[LWC Template] if:false={${prop}} = ${val}`);
            }
            if (val) return null;
        }

        // Handle for:each loops
        if (forEach) {
            const prop = forEach.replace(/^\{|\}$/g, '');
            const items = resolveBinding(state, prop) || [];
            const itemVar = node.getAttribute('for:item') || 'item';
            const indexVar = node.getAttribute('for:index');

            // Debug: log for:each loop info
            if (prop === 'technicianRoutes' || prop.includes('legDetails')) {
                console.log(`[LWC Template] for:each={${prop}} items:`, items?.length);
            }

            if (!Array.isArray(items) || items.length === 0) {
                return null;
            }

            return items.map((item, idx) => {
                const itemState = { ...state, [itemVar]: item };
                if (indexVar) itemState[indexVar] = idx;

                const children = Array.from(node.childNodes).map((child, childIdx) =>
                    processNode(child.cloneNode(true), itemState, handlers, `${key}-${idx}-${childIdx}`, componentName)
                ).filter(c => c !== null);

                return React.createElement(React.Fragment, { key: `loop-${idx}` }, children);
            });
        }

        // Process children of template without directive
        const children = Array.from(node.childNodes).map((child, idx) =>
            processNode(child, state, handlers, `${key}-${idx}`, componentName)
        ).filter(c => c !== null);

        return children.length > 0 ? React.createElement(React.Fragment, { key }, children) : null;
    }

    // Build props
    const props = { key: `${key}` };

    // Process attributes
    Array.from(node.attributes).forEach(attr => {
        let name = attr.name;
        let value = attr.value;

        // Skip LWC directives
        if (name.startsWith('if:') || name.startsWith('for:') || name.startsWith('lwc:') || name.startsWith('iterator:')) {
            return;
        }

        // Handle onclick, onchange -> onClick, onChange
        if (name.startsWith('on')) {
            const eventName = 'on' + name.charAt(2).toUpperCase() + name.slice(3);
            const handlerName = value.replace(/^\{|\}$/g, '');
            const handler = handlers[handlerName];
            if (!handler) {
                console.log(`[LWC] Handler '${handlerName}' not found for ${eventName}. Available:`, Object.keys(handlers));
            }
            props[eventName] = handler || handlers.handleGenericEvent || (() => {
                console.log(`[LWC] Unhandled event: ${handlerName}`);
            });
            return;
        }

        // Handle class -> className
        if (name === 'class') {
            name = 'className';
        }

        // Handle for -> htmlFor
        if (name === 'for') {
            name = 'htmlFor';
        }

        // Handle style attribute
        if (name === 'style') {
            // Convert CSS string to style object
            const styleObj = {};
            value.split(';').forEach(rule => {
                const [prop, val] = rule.split(':').map(s => s?.trim());
                if (prop && val) {
                    // Convert kebab-case to camelCase
                    const camelProp = prop.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                    styleObj[camelProp] = val;
                }
            });
            props.style = styleObj;
            return;
        }

        // Replace {property} bindings in attribute values
        if (value.includes('{')) {
            value = value.replace(/\{([\w.[\]]+)\}/g, (match, prop) => {
                const val = resolveBinding(state, prop);
                if (val === undefined || val === null) return '';
                if (typeof val === 'object') return JSON.stringify(val);
                return String(val);
            });
        }

        // Handle data- attributes
        if (name.startsWith('data-')) {
            props[name] = value;
            return;
        }

        // Skip empty values for boolean attributes
        if (value === '' && ['disabled', 'checked', 'readonly', 'required'].includes(name)) {
            props[name] = true;
            return;
        }

        props[name] = value;
    });

    // Convert Lightning components to React/HTML
    let ReactTagName = tagName;

    // Convert our lwc-* table elements back to regular table elements
    const tableElementMap = {
        'lwc-table': 'table',
        'lwc-thead': 'thead',
        'lwc-tbody': 'tbody',
        'lwc-tr': 'tr',
        'lwc-td': 'td',
        'lwc-th': 'th'
    };
    if (tableElementMap[tagName]) {
        ReactTagName = tableElementMap[tagName];
    }

    if (tagName.startsWith('lightning-')) {
        const result = convertLightningComponent(tagName, node, props, state, handlers, key, componentName);
        if (result) return result;
        ReactTagName = 'div';
        props.className = `${tagName} ${props.className || ''}`;
    }

    // Handle c- custom components (render as div with class)
    if (tagName.startsWith('c-')) {
        ReactTagName = 'div';
        props.className = `lwc-custom lwc-${tagName.substring(2)} ${props.className || ''}`;
    }

    // Process children
    const children = Array.from(node.childNodes).map((child, idx) =>
        processNode(child, state, handlers, `${key}-${idx}`, componentName)
    ).filter(child => child !== null);

    return React.createElement(ReactTagName, props, children.length > 0 ? children : undefined);
}

/**
 * Convert Lightning Base Components to React elements
 */
function convertLightningComponent(tagName, node, props, state, handlers, key, componentName) {
    const label = props.label || node.getAttribute('label') || '';
    const variant = props.variant || node.getAttribute('variant') || 'neutral';
    const value = props.value || node.getAttribute('value') || '';
    const name = props.name || node.getAttribute('name') || '';
    const placeholder = props.placeholder || node.getAttribute('placeholder') || '';

    switch (tagName) {
        case 'lightning-card': {
            const title = props.title || node.getAttribute('title') || '';
            const iconName = node.getAttribute('icon-name');
            const children = Array.from(node.childNodes).map((child, idx) =>
                processNode(child, state, handlers, `${key}-card-${idx}`, componentName)
            ).filter(c => c !== null);

            return React.createElement('article', {
                key,
                className: `slds-card ${props.className || ''}`
            }, [
                React.createElement('div', {
                    key: 'header',
                    className: 'slds-card__header slds-grid'
                },
                    React.createElement('header', {
                        className: 'slds-media slds-media_center slds-has-flexi-truncate'
                    }, [
                        iconName && React.createElement('div', {
                            key: 'icon',
                            className: 'slds-media__figure'
                        }, React.createElement('span', {
                            className: `slds-icon_container slds-icon-${iconName.replace(':', '-')}`
                        })),
                        React.createElement('div', {
                            key: 'body',
                            className: 'slds-media__body'
                        },
                            React.createElement('h2', {
                                className: 'slds-card__header-title'
                            },
                                React.createElement('span', null, title)
                            )
                        )
                    ])
                ),
                React.createElement('div', {
                    key: 'body',
                    className: 'slds-card__body slds-card__body_inner'
                }, children)
            ]);
        }

        case 'lightning-button': {
            const btnClass = variant === 'brand' ? 'slds-button_brand' :
                           variant === 'destructive' ? 'slds-button_destructive' :
                           variant === 'success' ? 'slds-button_success' :
                           variant === 'outline-brand' ? 'slds-button_outline-brand' :
                           'slds-button_neutral';

            // Get onclick handler from node attribute if not in props
            let clickHandler = props.onClick;
            if (!clickHandler) {
                const onclickAttr = node.getAttribute('onclick');
                if (onclickAttr) {
                    const handlerName = onclickAttr.replace(/^\{|\}$/g, '');
                    clickHandler = handlers[handlerName];
                    console.log(`[LWC Button] Looking for handler '${handlerName}':`, !!clickHandler);
                }
            }

            return React.createElement('button', {
                key,
                className: `slds-button ${btnClass} ${props.className || ''}`,
                onClick: clickHandler,
                disabled: props.disabled === 'true' || props.disabled === true,
                type: props.type || 'button'
            }, label);
        }

        case 'lightning-button-icon': {
            const iconName = node.getAttribute('icon-name') || '';
            const iconMap = {
                'utility:add': '+',
                'utility:close': '×',
                'utility:delete': '🗑',
                'utility:edit': '✏',
                'utility:refresh': '↻',
                'utility:search': '🔍',
                'utility:check': '✓',
                'utility:chevronright': '›',
                'utility:chevronleft': '‹',
                'utility:chevrondown': '▼',
                'utility:chevronup': '▲',
                'utility:drag_and_drop': '⋮⋮',
                'utility:settings': '⚙',
                'utility:warning': '⚠',
                'utility:error': '❌',
                'utility:success': '✅',
                'utility:info': 'ℹ',
                'utility:arrow_up': '↑',
                'utility:arrow_down': '↓',
                'utility:lock': '🔒',
                'utility:drag': '⋮⋮',
            };

            // Get onclick handler from node attribute if not in props
            let clickHandler = props.onClick;
            if (!clickHandler) {
                const onclickAttr = node.getAttribute('onclick');
                if (onclickAttr) {
                    const handlerName = onclickAttr.replace(/^\{|\}$/g, '');
                    clickHandler = handlers[handlerName];
                }
            }

            return React.createElement('button', {
                key,
                className: `slds-button slds-button_icon ${props.className || ''}`,
                onClick: clickHandler,
                title: props.title || node.getAttribute('alternative-text') || '',
                type: 'button',
                'data-id': node.getAttribute('data-id') || '',
                'data-technician-id': node.getAttribute('data-technician-id') || ''
            }, iconMap[iconName] || '•');
        }

        case 'lightning-input': {
            const type = props.type || node.getAttribute('type') || 'text';
            const required = node.hasAttribute('required');
            const disabled = node.hasAttribute('disabled');
            const readOnly = node.hasAttribute('readonly');

            return React.createElement('div', {
                key,
                className: 'slds-form-element'
            }, [
                label && React.createElement('label', {
                    key: 'label',
                    className: 'slds-form-element__label'
                }, [
                    required && React.createElement('abbr', {
                        key: 'req',
                        className: 'slds-required',
                        title: 'required'
                    }, '*'),
                    label
                ]),
                React.createElement('div', {
                    key: 'control',
                    className: 'slds-form-element__control'
                },
                    type === 'checkbox' ?
                        React.createElement('label', { className: 'slds-checkbox' }, [
                            React.createElement('input', {
                                key: 'input',
                                type: 'checkbox',
                                name,
                                checked: value === 'true' || value === true,
                                onChange: props.onChange || handlers.handleChange,
                                disabled
                            }),
                            React.createElement('span', { key: 'faux', className: 'slds-checkbox_faux' }),
                            React.createElement('span', { key: 'label', className: 'slds-form-element__label' })
                        ]) :
                        React.createElement('input', {
                            key: 'input',
                            type,
                            className: 'slds-input',
                            name,
                            value: resolveBinding(state, value.replace(/^\{|\}$/g, '')) || value,
                            placeholder,
                            onChange: props.onChange || handlers.handleChange,
                            required,
                            disabled,
                            readOnly
                        })
                )
            ]);
        }

        case 'lightning-combobox': {
            const optionsAttr = node.getAttribute('options');
            const valueAttr = node.getAttribute('value');
            let options = [];
            let currentValue = '';

            if (optionsAttr) {
                const optionsKey = optionsAttr.replace(/^\{|\}$/g, '');
                options = resolveBinding(state, optionsKey) || [];
            }

            if (valueAttr) {
                const valueKey = valueAttr.replace(/^\{|\}$/g, '');
                currentValue = resolveBinding(state, valueKey) || '';
            }

            const onchangeAttr = node.getAttribute('onchange');
            const onChangeHandler = onchangeAttr ?
                handlers[onchangeAttr.replace(/^\{|\}$/g, '')] :
                handlers.handleChange;

            return React.createElement('div', {
                key,
                className: 'slds-form-element'
            }, [
                label && React.createElement('label', {
                    key: 'label',
                    className: 'slds-form-element__label'
                }, label),
                React.createElement('div', {
                    key: 'control',
                    className: 'slds-form-element__control'
                },
                    React.createElement('select', {
                        className: 'slds-select',
                        name,
                        value: currentValue,
                        onChange: onChangeHandler
                    }, [
                        React.createElement('option', { key: 'placeholder', value: '' }, placeholder || 'בחר...'),
                        ...options.map((opt, i) =>
                            React.createElement('option', {
                                key: i,
                                value: opt.value || opt.Id || opt
                            }, opt.label || opt.Name || opt.value || opt)
                        )
                    ])
                )
            ]);
        }

        case 'lightning-textarea': {
            return React.createElement('div', {
                key,
                className: 'slds-form-element'
            }, [
                label && React.createElement('label', {
                    key: 'label',
                    className: 'slds-form-element__label'
                }, label),
                React.createElement('div', {
                    key: 'control',
                    className: 'slds-form-element__control'
                },
                    React.createElement('textarea', {
                        className: 'slds-textarea',
                        name,
                        value: resolveBinding(state, value.replace(/^\{|\}$/g, '')) || '',
                        placeholder,
                        onChange: props.onChange || handlers.handleChange,
                        rows: props.rows || 3
                    })
                )
            ]);
        }

        case 'lightning-spinner': {
            const size = node.getAttribute('size') || 'medium';
            const sizeMap = { 'x-small': '16px', 'small': '24px', 'medium': '32px', 'large': '48px' };
            const spinnerSize = sizeMap[size] || '32px';
            // Use simple CSS spinner instead of Afikim UI (which needs complex styling)
            return React.createElement('div', {
                key,
                className: 'lwc-spinner-container',
                style: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                    minHeight: '80px'
                }
            },
                React.createElement('div', {
                    className: 'lwc-spinner',
                    style: {
                        width: spinnerSize,
                        height: spinnerSize,
                        border: '3px solid #e0e5ee',
                        borderTopColor: '#0070d2',
                        borderRadius: '50%',
                        animation: 'lwc-spin 0.8s linear infinite'
                    }
                })
            );
        }

        case 'lightning-datatable': {
            const columnsAttr = node.getAttribute('columns');
            const dataAttr = node.getAttribute('data');
            const keyFieldAttr = node.getAttribute('key-field') || 'Id';

            let columns = [];
            let data = [];

            if (columnsAttr) {
                columns = resolveBinding(state, columnsAttr.replace(/^\{|\}$/g, '')) || [];
            }
            if (dataAttr) {
                data = resolveBinding(state, dataAttr.replace(/^\{|\}$/g, '')) || [];
            }

            return React.createElement('table', {
                key,
                className: 'slds-table slds-table_cell-buffer slds-table_bordered'
            }, [
                React.createElement('thead', { key: 'thead' },
                    React.createElement('tr', { className: 'slds-line-height_reset' },
                        columns.map((col, i) =>
                            React.createElement('th', {
                                key: i,
                                scope: 'col',
                                className: 'slds-text-title_caps'
                            }, col.label || col.fieldName)
                        )
                    )
                ),
                React.createElement('tbody', { key: 'tbody' },
                    data.map((row, rowIdx) =>
                        React.createElement('tr', { key: row[keyFieldAttr] || rowIdx },
                            columns.map((col, colIdx) =>
                                React.createElement('td', {
                                    key: colIdx,
                                    'data-label': col.label
                                }, row[col.fieldName] || '')
                            )
                        )
                    )
                )
            ]);
        }

        case 'lightning-badge': {
            return React.createElement('span', {
                key,
                className: `slds-badge ${props.className || ''}`
            }, label);
        }

        case 'lightning-formatted-date-time': {
            const dateValue = props.value || '';
            let formatted = dateValue;
            try {
                if (dateValue) {
                    const date = new Date(dateValue);
                    formatted = date.toLocaleDateString('he-IL') + ' ' + date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
                }
            } catch (e) {
                formatted = dateValue;
            }
            return React.createElement('span', { key, className: 'slds-truncate' }, formatted);
        }

        case 'lightning-formatted-number':
        case 'lightning-formatted-text': {
            return React.createElement('span', { key }, props.value || '');
        }

        case 'lightning-icon': {
            const iconName = node.getAttribute('icon-name') || '';
            const size = node.getAttribute('size') || 'small';
            const iconMap = {
                // Utility icons
                'utility:warning': '⚠️',
                'utility:error': '❌',
                'utility:success': '✅',
                'utility:info': 'ℹ️',
                'utility:check': '✓',
                'utility:close': '×',
                'utility:user': '👤',
                'utility:location': '📍',
                'utility:clock': '🕐',
                'utility:event': '📅',
                'utility:record': '📋',
                'utility:trending': '📈',
                'utility:forward': '➡️',
                // Standard icons
                'standard:account': '🏢',
                'standard:contact': '👤',
                'standard:opportunity': '💰',
                'standard:service_resource': '👷',
                'standard:service_appointment': '📋',
                'standard:service_territory': '🗺️',
                'standard:user': '👤',
                // Action icons
                'action:edit': '✏️',
                'action:delete': '🗑️',
            };
            return React.createElement('span', {
                key,
                className: `slds-icon slds-icon_${size} ${props.className || ''}`,
                title: node.getAttribute('alternative-text') || '',
                style: { fontSize: size === 'x-small' ? '12px' : size === 'small' ? '16px' : size === 'medium' ? '24px' : size === 'large' ? '32px' : '16px' }
            }, iconMap[iconName] || '●');
        }

        case 'lightning-progress-bar': {
            const progressValue = parseInt(props.value) || 0;
            return React.createElement('div', {
                key,
                className: 'slds-progress-bar',
                'aria-valuemin': '0',
                'aria-valuemax': '100',
                'aria-valuenow': progressValue,
                role: 'progressbar'
            },
                React.createElement('span', {
                    className: 'slds-progress-bar__value',
                    style: { width: `${progressValue}%` }
                })
            );
        }

        case 'lightning-accordion':
        case 'lightning-accordion-section': {
            // Render as expandable section
            const sectionLabel = node.getAttribute('label') || props.label || '';
            const children = Array.from(node.childNodes).map((child, idx) =>
                processNode(child, state, handlers, `${key}-acc-${idx}`, componentName)
            ).filter(c => c !== null);

            if (tagName === 'lightning-accordion') {
                return React.createElement('div', {
                    key,
                    className: 'slds-accordion'
                }, children);
            }

            return React.createElement('section', {
                key,
                className: 'slds-accordion__section slds-is-open'
            }, [
                React.createElement('div', {
                    key: 'summary',
                    className: 'slds-accordion__summary'
                },
                    React.createElement('h2', {
                        className: 'slds-accordion__summary-heading'
                    },
                        React.createElement('button', {
                            className: 'slds-button slds-button_reset slds-accordion__summary-action',
                            type: 'button'
                        }, [
                            React.createElement('span', { key: 'title', className: 'slds-accordion__summary-content' }, sectionLabel)
                        ])
                    )
                ),
                React.createElement('div', {
                    key: 'content',
                    className: 'slds-accordion__content'
                }, children)
            ]);
        }

        case 'lightning-tab':
        case 'lightning-tabset': {
            const children = Array.from(node.childNodes).map((child, idx) =>
                processNode(child, state, handlers, `${key}-tab-${idx}`, componentName)
            ).filter(c => c !== null);

            if (tagName === 'lightning-tabset') {
                return React.createElement('div', {
                    key,
                    className: 'slds-tabs_default'
                }, children);
            }

            const tabLabel = node.getAttribute('label') || '';
            return React.createElement('div', {
                key,
                className: 'slds-tabs_default__content slds-show',
                role: 'tabpanel'
            }, [
                React.createElement('h3', { key: 'label', className: 'slds-text-heading_small' }, tabLabel),
                ...children
            ]);
        }

        case 'lightning-map': {
            // Get map markers from attribute binding
            const markersAttr = node.getAttribute('map-markers');
            const centerAttr = node.getAttribute('center');
            const zoomLevel = parseInt(node.getAttribute('zoom-level')) || 12;
            const markersTitle = node.getAttribute('markers-title') || 'מיקומים';

            let markers = [];
            let center = { Latitude: 32.0853, Longitude: 34.7818 }; // Default Tel Aviv

            if (markersAttr) {
                const markersKey = markersAttr.replace(/^\{|\}$/g, '');
                markers = resolveBinding(state, markersKey) || [];
            }

            if (centerAttr) {
                const centerKey = centerAttr.replace(/^\{|\}$/g, '');
                center = resolveBinding(state, centerKey) || center;
            }

            // If no markers, don't render the map
            if (!markers || markers.length === 0) {
                return null;
            }

            // Calculate center from markers
            const validMarkers = markers.filter(m => {
                const lat = m.location?.Latitude || m.Latitude;
                const lng = m.location?.Longitude || m.Longitude;
                return lat && lng;
            });

            if (validMarkers.length > 0) {
                const avgLat = validMarkers.reduce((sum, m) => sum + parseFloat(m.location?.Latitude || m.Latitude), 0) / validMarkers.length;
                const avgLng = validMarkers.reduce((sum, m) => sum + parseFloat(m.location?.Longitude || m.Longitude), 0) / validMarkers.length;
                center = { Latitude: avgLat, Longitude: avgLng };
            }

            // Use Google Maps Static API with markers (uses env key)
            const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

            // Build markers string for Google Static Maps
            // Note: Google Static Maps only supports single-character labels (1-9, A-Z)
            // For markers 10+, we use custom numbered icons
            const markerParams = validMarkers.map((m, idx) => {
                const lat = m.location?.Latitude || m.Latitude;
                const lng = m.location?.Longitude || m.Longitude;
                const markerNum = idx + 1;
                // Use custom numbered marker icons from a public service
                const iconUrl = `https://chart.apis.google.com/chart?chst=d_map_pin_letter%26chld=${markerNum}%7CFE7569%7C000000`;
                return `markers=icon:${encodeURIComponent(iconUrl)}%7C${lat},${lng}`;
            }).join('&');

            const staticMapUrl = googleApiKey
                ? `https://maps.googleapis.com/maps/api/staticmap?center=${center.Latitude},${center.Longitude}&zoom=${zoomLevel}&size=800x400&maptype=roadmap&${markerParams}&key=${googleApiKey}`
                : null;

            // Create marker list for display alongside map
            // Each marker is clickable to show details popup on the map
            const markerListItems = validMarkers.slice(0, 50).map((m, idx) => {
                const title = m.title || `נקודה ${idx + 1}`;
                const desc = m.description || '';
                const markerNum = idx + 1; // Always use actual number
                const lat = m.location?.Latitude || m.Latitude;
                const lng = m.location?.Longitude || m.Longitude;
                const accountName = m.accountName || '';

                return React.createElement('div', {
                    key: `marker-${idx}`,
                    className: 'marker-list-item',
                    'data-marker-idx': idx,
                    onClick: (e) => {
                        // Highlight this item
                        const container = e.target.closest('.lwc-map-container');
                        if (container) {
                            // Remove previous highlights
                            container.querySelectorAll('.marker-list-item.highlighted').forEach(el => {
                                el.classList.remove('highlighted');
                                el.style.background = '';
                            });
                            // Highlight current
                            const item = e.target.closest('.marker-list-item');
                            if (item) {
                                item.classList.add('highlighted');
                                item.style.background = '#d4edda';
                            }

                            // Show info popup on map area
                            const infoPopup = container.querySelector('.map-info-popup');
                            if (infoPopup) {
                                infoPopup.innerHTML = `
                                    <div style="padding: 10px; background: #fff; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.2); max-width: 250px;">
                                        <div style="font-weight: bold; color: #16325c; font-size: 14px; margin-bottom: 5px;">${markerNum}. ${title}</div>
                                        ${accountName ? `<div style="color: #54698d; font-size: 12px; margin-bottom: 3px;">לקוח: ${accountName}</div>` : ''}
                                        <div style="color: #706e6b; font-size: 12px;">${desc || 'אין כתובת'}</div>
                                        <div style="color: #999; font-size: 11px; margin-top: 5px;">${lat ? `${lat}, ${lng}` : 'אין קואורדינטות'}</div>
                                    </div>
                                `;
                                infoPopup.style.display = 'block';
                            }
                        }
                    },
                    style: {
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 10px',
                        borderBottom: '1px solid #e5e5e5',
                        fontSize: '13px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                    },
                    onMouseEnter: (e) => {
                        if (!e.target.closest('.marker-list-item.highlighted')) {
                            e.target.closest('.marker-list-item').style.background = '#f5f5f5';
                        }
                    },
                    onMouseLeave: (e) => {
                        if (!e.target.closest('.marker-list-item.highlighted')) {
                            e.target.closest('.marker-list-item').style.background = '';
                        }
                    }
                }, [
                    React.createElement('span', {
                        key: 'num',
                        style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '26px',
                            height: '26px',
                            borderRadius: '50%',
                            background: '#ea4335',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            marginLeft: '10px',
                            flexShrink: 0
                        }
                    }, markerNum),
                    React.createElement('div', { key: 'info', style: { flex: 1, minWidth: 0 } }, [
                        React.createElement('div', { key: 'title', style: { fontWeight: '500', color: '#16325c' } }, title),
                        accountName && React.createElement('div', { key: 'account', style: { fontSize: '11px', color: '#54698d' } }, accountName),
                        desc && React.createElement('div', { key: 'desc', style: { fontSize: '11px', color: '#706e6b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, desc)
                    ])
                ]);
            });

            return React.createElement('div', {
                key,
                className: 'lwc-map-container',
                style: {
                    width: '100%',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid #d8d8d8',
                    marginBottom: '16px',
                    background: '#fff'
                }
            }, [
                React.createElement('div', {
                    key: 'header',
                    style: {
                        padding: '12px 16px',
                        background: 'linear-gradient(to bottom, #fafaf9, #f3f2f2)',
                        borderBottom: '1px solid #d8d8d8',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#16325c',
                        display: 'flex',
                        alignItems: 'center'
                    }
                }, [
                    React.createElement('span', { key: 'icon', style: { marginLeft: '8px' } }, '🗺️'),
                    `${markersTitle} (${validMarkers.length} נקודות)`
                ]),
                React.createElement('div', {
                    key: 'content',
                    style: {
                        display: 'flex',
                        height: '350px'
                    }
                }, [
                    // Map image or fallback
                    React.createElement('div', {
                        key: 'map-frame',
                        style: { flex: '1', minWidth: '65%', position: 'relative', background: '#e5e5e5' }
                    }, [
                        staticMapUrl ? React.createElement('img', {
                            key: 'map-img',
                            src: staticMapUrl,
                            alt: markersTitle,
                            style: {
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            },
                            onError: (e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }
                        }) : null,
                        React.createElement('div', {
                            key: 'fallback',
                            style: {
                                display: staticMapUrl ? 'none' : 'flex',
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                color: '#706e6b',
                                background: '#f4f6f9'
                            }
                        }, [
                            React.createElement('div', { key: 'icon', style: { fontSize: '48px', marginBottom: '12px' } }, '🗺️'),
                            React.createElement('div', { key: 'text' }, 'מפה לא זמינה - בדוק מפתח Google Maps')
                        ]),
                        // Info popup that appears when clicking a marker in the list
                        React.createElement('div', {
                            key: 'info-popup',
                            className: 'map-info-popup',
                            style: {
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                display: 'none',
                                zIndex: 100
                            }
                        })
                    ]),
                    // Markers list
                    React.createElement('div', {
                        key: 'markers-list',
                        style: {
                            width: '220px',
                            borderRight: '1px solid #e5e5e5',
                            overflowY: 'auto',
                            background: '#fafaf9'
                        }
                    }, markerListItems.length > 0 ? markerListItems :
                        React.createElement('div', { style: { padding: '16px', color: '#706e6b', textAlign: 'center' } }, 'אין נקודות להצגה')
                    )
                ])
            ]);
        }

        case 'lightning-button-group': {
            // Render children buttons in a group
            const childButtons = [];
            for (const child of node.childNodes) {
                if (child.nodeType === 1) {
                    const childElement = processNode(child, state, handlers, `${key}-btn-${childButtons.length}`, componentName);
                    if (childElement) childButtons.push(childElement);
                }
            }
            return React.createElement('div', {
                key,
                className: 'slds-button-group',
                role: 'group'
            }, childButtons);
        }

        case 'lightning-modal':
        case 'lightning-modal-header':
        case 'lightning-modal-body':
        case 'lightning-modal-footer': {
            const modalTagName = node.tagName.toLowerCase();
            const modalChildElements = [];
            for (const child of node.childNodes) {
                if (child.nodeType === 1) {
                    const childElement = processNode(child, state, handlers, `${key}-${modalChildElements.length}`, componentName);
                    if (childElement) modalChildElements.push(childElement);
                } else if (child.nodeType === 3 && child.textContent.trim()) {
                    modalChildElements.push(child.textContent.trim());
                }
            }

            const classMap = {
                'lightning-modal': 'slds-modal slds-fade-in-open',
                'lightning-modal-header': 'slds-modal__header',
                'lightning-modal-body': 'slds-modal__content slds-p-around_medium',
                'lightning-modal-footer': 'slds-modal__footer'
            };

            return React.createElement('div', {
                key,
                className: classMap[modalTagName] || ''
            }, modalChildElements);
        }

        case 'lightning-quick-action-panel': {
            const panelChildElements = [];
            for (const child of node.childNodes) {
                if (child.nodeType === 1) {
                    const childElement = processNode(child, state, handlers, `${key}-${panelChildElements.length}`, componentName);
                    if (childElement) panelChildElements.push(childElement);
                } else if (child.nodeType === 3 && child.textContent.trim()) {
                    panelChildElements.push(child.textContent.trim());
                }
            }
            return React.createElement('div', {
                key,
                className: 'slds-panel slds-panel_docked slds-is-open'
            }, panelChildElements);
        }

        case 'lightning-flow': {
            const flowApiName = node.getAttribute('flow-api-name');
            return React.createElement('div', {
                key,
                className: 'slds-box slds-theme_default',
                style: { padding: '16px', textAlign: 'center' }
            }, [
                React.createElement('p', { key: 'msg' }, `Flow: ${flowApiName || 'לא הוגדר'}`),
                React.createElement('p', { key: 'note', style: { color: '#706e6b', fontSize: '12px' } }, 'Flows לא נתמכים במערכת זו')
            ]);
        }

        case 'lightning-record-edit-form':
        case 'lightning-record-view-form': {
            const formRecordId = node.getAttribute('record-id');
            const formObjectApiName = node.getAttribute('object-api-name');
            const formChildElements = [];
            for (const child of node.childNodes) {
                if (child.nodeType === 1) {
                    const childElement = processNode(child, state, handlers, `${key}-${formChildElements.length}`, componentName);
                    if (childElement) formChildElements.push(childElement);
                }
            }
            return React.createElement('div', {
                key,
                className: 'slds-form',
                'data-record-id': formRecordId,
                'data-object': formObjectApiName
            }, formChildElements);
        }

        case 'lightning-output-field':
        case 'lightning-input-field': {
            const fieldName = node.getAttribute('field-name');
            const fieldValue = state[fieldName] || '';
            const isOutput = node.tagName.toLowerCase() === 'lightning-output-field';

            return React.createElement('div', {
                key,
                className: 'slds-form-element'
            }, [
                React.createElement('label', {
                    key: 'label',
                    className: 'slds-form-element__label'
                }, fieldName),
                React.createElement('div', {
                    key: 'control',
                    className: 'slds-form-element__control'
                }, isOutput ?
                    React.createElement('span', { className: 'slds-form-element__static' }, fieldValue) :
                    React.createElement('input', {
                        type: 'text',
                        className: 'slds-input',
                        name: fieldName,
                        defaultValue: fieldValue,
                        onChange: handlers.handleChange
                    })
                )
            ]);
        }

        case 'lightning-helptext': {
            const helpContent = node.getAttribute('content') || '';
            return React.createElement('span', {
                key,
                className: 'slds-form-element__icon',
                title: helpContent,
                style: { cursor: 'help', color: '#0070d2' }
            }, 'ℹ️');
        }

        case 'lightning-pill': {
            const pillLabel = label || node.textContent?.trim() || '';
            return React.createElement('span', {
                key,
                className: 'slds-pill'
            }, [
                React.createElement('span', { key: 'label', className: 'slds-pill__label' }, pillLabel),
                React.createElement('button', {
                    key: 'remove',
                    className: 'slds-button slds-button_icon slds-pill__remove',
                    title: 'הסר',
                    onClick: handlers.handleRemovePill
                }, '×')
            ]);
        }

        case 'lightning-layout': {
            const multipleRows = node.hasAttribute('multiple-rows');
            const layoutChildElements = [];
            for (const child of node.childNodes) {
                if (child.nodeType === 1) {
                    const childElement = processNode(child, state, handlers, `${key}-${layoutChildElements.length}`, componentName);
                    if (childElement) layoutChildElements.push(childElement);
                }
            }
            return React.createElement('div', {
                key,
                className: 'slds-grid' + (multipleRows ? ' slds-wrap' : ''),
                style: { gap: '8px' }
            }, layoutChildElements);
        }

        case 'lightning-layout-item': {
            const itemSize = node.getAttribute('size') || '12';
            const flexibility = node.getAttribute('flexibility');
            const itemPadding = node.getAttribute('padding');

            const itemChildElements = [];
            for (const child of node.childNodes) {
                if (child.nodeType === 1) {
                    const childElement = processNode(child, state, handlers, `${key}-${itemChildElements.length}`, componentName);
                    if (childElement) itemChildElements.push(childElement);
                } else if (child.nodeType === 3 && child.textContent.trim()) {
                    itemChildElements.push(child.textContent.trim());
                }
            }

            const sizeClass = `slds-size_${itemSize}-of-12`;
            let itemClasses = `slds-col ${sizeClass}`;
            if (itemPadding) itemClasses += ` slds-p-around_${itemPadding}`;
            if (flexibility === 'auto') itemClasses += ' slds-grow';

            return React.createElement('div', {
                key,
                className: itemClasses
            }, itemChildElements);
        }

        default:
            return null;
    }
}

/**
 * Component Data Loader - identifies and fetches required data based on component
 */
async function loadComponentData(componentName, jsContent, htmlContent, initialState) {
    const loadedData = { ...initialState };

    try {
        // ============================================================
        // SALESFORCE PLATFORM IMPORTS - Handle @salesforce/* imports
        // ============================================================
        if (jsContent) {
            // Check for CURRENT_USER_ID import
            if (jsContent.includes("@salesforce/user/Id")) {
                // Get current user from localStorage or use demo user
                const storedUser = localStorage.getItem('currentUser');
                let userId = 'demo-user-001';
                if (storedUser) {
                    try {
                        const user = JSON.parse(storedUser);
                        userId = user.id || user.Id || userId;
                    } catch {}
                }
                loadedData.CURRENT_USER_ID = userId;
                loadedData.userId = userId;
                console.log(`[DynamicLWC] Set CURRENT_USER_ID: ${userId}`);
            }

            // Check for other @salesforce imports
            if (jsContent.includes("@salesforce/schema")) {
                // Schema imports are handled at compile time, we can ignore
                console.log('[DynamicLWC] Schema imports detected - skipping');
            }

            // Check for NavigationMixin
            if (jsContent.includes("lightning/navigation")) {
                loadedData._hasNavigation = true;
            }
        }

        // Parse @track and @api properties from JS
        if (jsContent) {
            // Extract @track properties with defaults
            const trackRegex = /@track\s+(\w+)\s*=?\s*([^;]*);?/g;
            let match;
            while ((match = trackRegex.exec(jsContent)) !== null) {
                const propName = match[1];
                let defaultValue = match[2]?.trim();

                if (defaultValue) {
                    try {
                        if (defaultValue.startsWith('[')) {
                            loadedData[propName] = JSON.parse(defaultValue);
                        } else if (defaultValue.startsWith('{')) {
                            loadedData[propName] = JSON.parse(defaultValue);
                        } else if (defaultValue === 'true') {
                            loadedData[propName] = true;
                        } else if (defaultValue === 'false') {
                            loadedData[propName] = false;
                        } else if (!isNaN(defaultValue) && defaultValue !== '') {
                            loadedData[propName] = Number(defaultValue);
                        } else {
                            loadedData[propName] = defaultValue.replace(/['"]/g, '');
                        }
                    } catch {
                        loadedData[propName] = defaultValue;
                    }
                } else {
                    loadedData[propName] = null;
                }
            }

            // Extract @api properties
            const apiRegex = /@api\s+(\w+)\s*;?/g;
            while ((match = apiRegex.exec(jsContent)) !== null) {
                if (loadedData[match[1]] === undefined) {
                    loadedData[match[1]] = null;
                }
            }
        }

        // Analyze HTML for data requirements
        if (htmlContent) {
            // Find all for:each loops to identify arrays needed
            const forEachRegex = /for:each=\{(\w+)\}/g;
            let match;
            while ((match = forEachRegex.exec(htmlContent)) !== null) {
                const arrayName = match[1];
                if (loadedData[arrayName] === undefined) {
                    loadedData[arrayName] = [];
                }
            }

            // Find all options bindings for comboboxes
            const optionsRegex = /options=\{(\w+)\}/g;
            while ((match = optionsRegex.exec(htmlContent)) !== null) {
                const optionsName = match[1];
                if (loadedData[optionsName] === undefined) {
                    loadedData[optionsName] = [];
                }
            }
        }

        // ============================================================
        // GENERIC DATA LOADING FROM JS - Parse @wire decorators
        // ============================================================
        if (jsContent) {
            // Handle getObjectInfo and getPicklistValues from lightning/uiObjectInfoApi
            if (jsContent.includes('getObjectInfo') || jsContent.includes('getPicklistValues')) {
                console.log('[DynamicLWC] Detected lightning/uiObjectInfoApi imports');

                // Extract schema imports like: SERVICE_RESOURCE_OBJECT from '@salesforce/schema/ServiceResource'
                const schemaObjectRegex = /import\s+(\w+)\s+from\s+['"]@salesforce\/schema\/(\w+)['"]/g;
                let schemaMatch;
                while ((schemaMatch = schemaObjectRegex.exec(jsContent)) !== null) {
                    const varName = schemaMatch[1];
                    const objectName = schemaMatch[2];
                    loadedData[varName] = { objectApiName: objectName };
                    console.log(`[DynamicLWC] Loaded schema object: ${varName} = ${objectName}`);
                }

                // Extract field schema imports like: TECH_TYPE from '@salesforce/schema/ServiceResource.Technical_Type__c'
                const schemaFieldRegex = /import\s+(\w+)\s+from\s+['"]@salesforce\/schema\/(\w+)\.(\w+)['"]/g;
                while ((schemaMatch = schemaFieldRegex.exec(jsContent)) !== null) {
                    const varName = schemaMatch[1];
                    const objectName = schemaMatch[2];
                    const fieldName = schemaMatch[3];
                    loadedData[varName] = { objectApiName: objectName, fieldApiName: fieldName };
                    console.log(`[DynamicLWC] Loaded schema field: ${varName} = ${objectName}.${fieldName}`);
                }

                // Find @wire(getObjectInfo) and provide mock objectInfo
                const objectInfoWireRegex = /@wire\s*\(\s*getObjectInfo[^)]*\)\s*(\w+)/g;
                let oiMatch;
                while ((oiMatch = objectInfoWireRegex.exec(jsContent)) !== null) {
                    const propName = oiMatch[1];
                    loadedData[propName] = {
                        data: {
                            defaultRecordTypeId: 'default-rt-001',
                            recordTypeInfos: {
                                'default-rt-001': { name: 'Default', defaultRecordTypeMapping: true }
                            },
                            fields: {}
                        }
                    };
                }

                // Find @wire(getPicklistValues) and provide picklist values
                const picklistWireRegex = /@wire\s*\(\s*getPicklistValues[^)]*\)\s*(\w+)/g;
                let plMatch;
                while ((plMatch = picklistWireRegex.exec(jsContent)) !== null) {
                    const propName = plMatch[1];
                    // Provide default technician types picklist
                    loadedData[propName] = {
                        data: {
                            values: [
                                { label: 'טכנאי שירות', value: 'טכנאי שירות' },
                                { label: 'טכנאי מכירה', value: 'טכנאי מכירה' },
                                { label: 'טכנאי התקנות', value: 'טכנאי התקנות' }
                            ]
                        }
                    };
                }
            }

            // Parse @wire decorators to auto-load data
            // Pattern: @wire(ControllerMethod) propertyName;
            // Pattern: import MethodName from '@salesforce/apex/ControllerName.methodName';
            const importRegex = /import\s+(\w+)\s+from\s+['"]@salesforce\/apex\/(\w+)\.(\w+)['"]/g;
            const wireRegex = /@wire\s*\(\s*(\w+)\s*\)\s*(\w+)/g;

            const importedMethods = {};
            let importMatch;
            while ((importMatch = importRegex.exec(jsContent)) !== null) {
                const localName = importMatch[1];
                const controllerName = importMatch[2];
                const methodName = importMatch[3];
                importedMethods[localName] = { controller: controllerName, method: methodName };
            }

            // Find @wire decorators and call the corresponding Apex methods
            let wireMatch;
            while ((wireMatch = wireRegex.exec(jsContent)) !== null) {
                const wireMethod = wireMatch[1];
                const propertyName = wireMatch[2];

                if (importedMethods[wireMethod]) {
                    const { controller, method } = importedMethods[wireMethod];
                    console.log(`[DynamicLWC] Auto-loading @wire(${wireMethod}) -> ${controller}.${method} into ${propertyName}`);

                    try {
                        // Pass recordId if available
                        const params = {};
                        if (initialState.recordId) {
                            params.recordId = initialState.recordId;
                            params.serviceAppointmentId = initialState.recordId;
                        }
                        const result = await callApex(controller, method, params);
                        loadedData[propertyName] = result;
                        console.log(`[DynamicLWC] Loaded ${propertyName}:`, Array.isArray(result) ? `${result.length} items` : typeof result);
                    } catch (e) {
                        console.warn(`[DynamicLWC] Failed to load wire data for ${propertyName}:`, e);
                        loadedData[propertyName] = [];
                    }
                }
            }

            // ============================================================
            // GENERIC APEX CALL EXECUTION - For any imported Apex method
            // ============================================================
            // Store all imported methods for runtime use
            loadedData._apexMethods = importedMethods;

            // Parse getter methods (computed properties) like: get totalOccupied() { ... }
            // AND execute them to get actual values
            const getterRegex = /get\s+(\w+)\s*\(\s*\)\s*\{([\s\S]*?)(?=\n\s{4}get\s|\n\s{4}async\s|\n\s{4}\w+\s*\(|\n\s{0,2}\})/g;
            const getterNames = [];
            const getterBodies = {};
            let getterMatch;
            while ((getterMatch = getterRegex.exec(jsContent)) !== null) {
                const getterName = getterMatch[1];
                const getterBody = getterMatch[2];
                getterNames.push(getterName);
                getterBodies[getterName] = getterBody;
            }

            // Store getter names for later computation
            loadedData._computedProperties = getterNames;
            loadedData._getterBodies = getterBodies;
            loadedData._importedMethods = importedMethods;

            // Execute simple getters that return static values
            for (const getterName of getterNames) {
                const body = getterBodies[getterName];
                if (body) {
                    // Try to extract simple return statements
                    // Pattern: return [...]; or return {...}; or return true/false/number
                    const returnMatch = body.match(/return\s+(\[[\s\S]*?\]|\{[\s\S]*?\}|true|false|\d+|'[^']*'|"[^"]*");?/);
                    if (returnMatch) {
                        try {
                            let returnValue = returnMatch[1].trim();
                            // Clean up the return value
                            if (returnValue.startsWith('[') || returnValue.startsWith('{')) {
                                // Try to parse as JSON (convert JS object literal to JSON)
                                returnValue = returnValue
                                    .replace(/(\w+):/g, '"$1":')  // Add quotes to keys
                                    .replace(/'/g, '"');          // Convert single to double quotes
                                loadedData[getterName] = JSON.parse(returnValue);
                            } else if (returnValue === 'true') {
                                loadedData[getterName] = true;
                            } else if (returnValue === 'false') {
                                loadedData[getterName] = false;
                            } else if (!isNaN(returnValue)) {
                                loadedData[getterName] = Number(returnValue);
                            } else {
                                loadedData[getterName] = returnValue.replace(/['"]/g, '');
                            }
                            console.log(`[DynamicLWC] Computed getter ${getterName}:`, loadedData[getterName]);
                        } catch (e) {
                            console.warn(`[DynamicLWC] Could not compute getter ${getterName}:`, e.message);
                        }
                    }

                    // Handle getters that check this.property
                    // Pattern: return this.someArray && this.someArray.length > 0;
                    const boolCheckMatch = body.match(/return\s+this\.(\w+)\s*&&\s*this\.\1\.length\s*>\s*0/);
                    if (boolCheckMatch) {
                        const arrayName = boolCheckMatch[1];
                        const arr = loadedData[arrayName];
                        loadedData[getterName] = Array.isArray(arr) && arr.length > 0;
                        console.log(`[DynamicLWC] Computed boolean getter ${getterName} from ${arrayName}:`, loadedData[getterName]);
                    }

                    // Handle negation getters like: return !this.isLoading;
                    const negationMatch = body.match(/return\s+!this\.(\w+)/);
                    if (negationMatch) {
                        const propName = negationMatch[1];
                        loadedData[getterName] = !loadedData[propName];
                        console.log(`[DynamicLWC] Computed negation getter ${getterName}:`, loadedData[getterName]);
                    }
                }
            }

            // Parse connectedCallback to find initialization logic
            if (jsContent.includes('connectedCallback')) {
                // Look for method calls inside connectedCallback
                const connectedMatch = jsContent.match(/connectedCallback\s*\(\s*\)\s*\{([^}]+)/);
                if (connectedMatch) {
                    const callbackBody = connectedMatch[1];
                    // Find this.loadXXX() or this.fetchXXX() calls
                    const methodCalls = callbackBody.match(/this\.(\w+)\(\)/g) || [];
                    for (const call of methodCalls) {
                        const methodName = call.match(/this\.(\w+)/)?.[1];
                        if (methodName && methodName.startsWith('load')) {
                            // Try to find what this method loads
                            const methodMatch = jsContent.match(new RegExp(`async\\s+${methodName}\\s*\\([^)]*\\)\\s*\\{([\\s\\S]*?)\\n\\s{4}\\}`));
                            if (methodMatch) {
                                // Look for Apex calls in the method body
                                const apexCallMatch = methodMatch[1].match(/await\s+(\w+)\s*\(/);
                                if (apexCallMatch && importedMethods[apexCallMatch[1]]) {
                                    const { controller, method } = importedMethods[apexCallMatch[1]];
                                    console.log(`[DynamicLWC] Found ${methodName}() calls ${controller}.${method}`);

                                    try {
                                        const result = await callApex(controller, method, {});
                                        // Try to determine property name from method body
                                        const assignMatch = methodMatch[1].match(/this\.(\w+)\s*=\s*(?:data|result|await)/);
                                        if (assignMatch) {
                                            loadedData[assignMatch[1]] = result;
                                        }
                                    } catch (e) {
                                        console.warn(`[DynamicLWC] Failed to call ${controller}.${method}:`, e);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // ============================================================
        // COMPONENT-SPECIFIC DATA LOADING (legacy, will be phased out)
        // ============================================================
        const componentLower = componentName.toLowerCase();

        // Technician Route Optimizer
        if (componentLower.includes('technicianrouteoptimizer')) {
            await loadTechnicianRouteOptimizerData(loadedData);
        }

        // Technician Scheduler
        if (componentLower.includes('technicianscheduler')) {
            await loadTechnicianSchedulerData(loadedData);
        }

        // Inventory Setup - load zones data
        if (componentLower.includes('inventorysetup')) {
            await loadInventorySetupData(loadedData);
        }

        // Address Validation
        if (componentLower.includes('addressvalidation')) {
            loadedData.isLoading = false;
            loadedData.hasError = false;
        }

        // Technician Selector - Load full data
        if (componentLower.includes('technicianselector')) {
            await loadTechnicianSelectorData(loadedData, initialState);
        }

        // ============================================================
        // GENERIC MODAL HANDLING
        // ============================================================
        const hasModalProperty = loadedData.showModal !== undefined ||
            (htmlContent && htmlContent.includes('if:true={showModal}'));

        if (hasModalProperty && loadedData.showModal !== true && initialState.openAsModal !== false) {
            loadedData.showModal = true;
            console.log(`[DynamicLWC] Auto-opening modal for ${componentName}`);
        }

        console.log(`[DynamicLWC] Component ${componentName} ready, showModal=${loadedData.showModal}`);

        loadedData.loading = false;
        loadedData.isLoading = false;

    } catch (error) {
        console.error('[DynamicLWC] Error loading component data:', error);
        loadedData.error = error.message;
        loadedData.loading = false;
    }

    return loadedData;
}

/**
 * Load data for Technician Route Optimizer component
 */
async function loadTechnicianRouteOptimizerData(data) {
    console.log('[DynamicLWC] Loading TechnicianRouteOptimizer data...');

    // Load all technicians first to extract unique types
    let allTechnicians = [];
    try {
        console.log('[DynamicLWC] Calling TechnicianRouteController.getTechniciansByType...');
        allTechnicians = await callApex('TechnicianRouteController', 'getTechniciansByType', {
            technicianType: 'כל סוגי הטכנאים'
        }) || [];
        console.log('[DynamicLWC] Got technicians:', allTechnicians.length);
    } catch (e) {
        console.warn('[DynamicLWC] Failed to load technicians:', e);
        // Try alternative API
        try {
            const response = await fetch(`${API_BASE}/api/objects/ServiceResource/records?limit=100`);
            if (response.ok) {
                const result = await response.json();
                allTechnicians = (result.records || []).filter(r => r.IsActive);
                console.log('[DynamicLWC] Got technicians from fallback:', allTechnicians.length);
            }
        } catch (e2) {
            console.warn('[DynamicLWC] Fallback also failed:', e2);
        }
    }

    // Extract unique technician types from actual data
    const uniqueTypes = new Set();
    allTechnicians.forEach(t => {
        const type = t.Technical_Type__c || t.ResourceType;
        if (type) {
            uniqueTypes.add(type);
        }
    });

    // Build technician type options from real data
    data.technicianTypeOptions = [{ label: 'כל סוגי הטכנאים', value: 'כל סוגי הטכנאים' }];
    if (uniqueTypes.size > 0) {
        Array.from(uniqueTypes).sort().forEach(type => {
            data.technicianTypeOptions.push({ label: type, value: type });
        });
    } else {
        // Fallback static options
        data.technicianTypeOptions.push(
            { label: 'טכנאי שירות', value: 'טכנאי שירות' },
            { label: 'טכנאי התקנות', value: 'טכנאי התקנות' }
        );
    }

    // Route type options (static)
    data.routeTypeOptions = [
        { label: 'קסם (מנחם)', value: 'geoNearest' },
        { label: 'קצר (Google)', value: 'short' }
    ];

    // Store all technicians and build options
    data.allTechnicians = allTechnicians;
    data.technicianOptions = [{ label: 'כל הטכנאים', value: 'all' }];
    if (allTechnicians.length > 0) {
        data.technicianOptions.push(...allTechnicians.map(t => ({
            label: t.Name,
            value: t.Id,
            type: t.Technical_Type__c || t.ResourceType
        })));
    }

    // Set defaults - use both naming conventions for compatibility
    data.selectedTechnicianType = 'כל סוגי הטכנאים';
    data.technicianType = 'כל סוגי הטכנאים';  // alias for LWC template
    data.selectedTechnician = 'all';
    data.selectedTechnicianId = 'all';  // alias for LWC template
    data.scheduleDate = new Date().toISOString().split('T')[0];
    data.routeGroups = [];
    data.appointments = [];
    data.technicianRoutes = [];
    data.hasRoutes = false;
    data.loading = false;
    data.isOptimizing = false;
    data.showResults = false;
    data.isAnyLoading = false;
    data.disableConfirmButton = true;

    console.log('[DynamicLWC] TechnicianRouteOptimizer data ready:', {
        technicianTypeOptions: data.technicianTypeOptions.length,
        technicianOptions: data.technicianOptions.length,
        scheduleDate: data.scheduleDate
    });
}

/**
 * Load data for Technician Scheduler component
 */
async function loadTechnicianSchedulerData(data) {
    try {
        const technicians = await callApex('TechnicianSchedulerService', 'getTechnicians', {});
        data.technicians = technicians || [];
        data.selectedTechnician = null;
        data.schedule = [];
    } catch (e) {
        console.warn('[DynamicLWC] Failed to load scheduler data:', e);
        data.technicians = [];
    }
}

/**
 * Load data for Technician Selector component
 */
async function loadTechnicianSelectorData(data, initialState) {
    console.log('[DynamicLWC] Loading TechnicianSelector data...');

    try {
        // Load parent territories
        let parentTerritories = [];
        try {
            parentTerritories = await callApex('TechnicianSelectorService', 'getParentTerritories', {});
            console.log('[DynamicLWC] Got parent territories:', parentTerritories?.length);
        } catch (e) {
            console.warn('[DynamicLWC] Failed to load parent territories:', e);
        }

        data.parentTerritories = parentTerritories || [];
        data.parentTerritoryOptions = (data.parentTerritories || []).map(t => ({
            label: t.name || t.Name,
            value: t.id || t.Id
        }));

        // Initialize state
        data.isLoading = false;
        data.initialLoadComplete = true;
        data.selectedParentTerritoryId = '';
        data.selectedTerritoryId = '';
        data.childTerritories = [];
        data.childTerritoryOptions = [];
        data.technicians = [];
        data.allTechnicians = [];
        data.dayHeaders = [];

        // Check if we have a recordId (service appointment)
        const saId = initialState.recordId;

        if (saId) {
            data.serviceAppointmentId = saId;

            // Check current assignment
            try {
                const currentAssignment = await callApex('TechnicianSelectorService', 'getCurrentAssignment', {
                    serviceAppointmentId: saId
                });
                console.log('[DynamicLWC] Current assignment:', currentAssignment);

                if (currentAssignment && currentAssignment.isScheduled) {
                    // Already scheduled - show the assignment details
                    data.isAlreadyScheduled = true;
                    data.currentAssignment = currentAssignment;
                    data.appointmentNumber = currentAssignment.appointmentNumber;
                    data.showFiltersSection = false;
                    data.showTechnicianTable = false;
                    data.showEmptyState = false;
                } else {
                    // Not scheduled - show the technician selector
                    data.isAlreadyScheduled = false;
                    data.appointmentNumber = currentAssignment?.appointmentNumber || saId;
                    data.showFiltersSection = true;
                    data.showTechnicianTable = false;
                    data.showEmptyState = true;

                    // Get territory from service appointment
                    try {
                        const territory = await callApex('TechnicianSelectorService', 'getTerritoryFromServiceAppointment', {
                            serviceAppointmentId: saId
                        });
                        console.log('[DynamicLWC] Territory from SA:', territory);

                        if (territory) {
                            data.currentTerritoryName = territory.name;
                            data.originalTerritoryId = territory.id;
                            data.originalTerritoryName = territory.name;
                            data.customerAddress = territory.customerAddress || '';
                            data.showCurrentTerritoryInfo = true;

                            // Set parent territory
                            if (territory.parentId) {
                                data.selectedParentTerritoryId = territory.parentId;

                                // Load child territories
                                const childTerritories = await callApex('TechnicianSelectorService', 'getChildTerritories', {
                                    parentTerritoryId: territory.parentId
                                });
                                data.childTerritories = childTerritories || [];
                                data.childTerritoryOptions = [
                                    { label: 'כל אזורי השירות', value: 'ALL' },
                                    ...data.childTerritories.map(t => ({
                                        label: t.id === data.originalTerritoryId ? `✓ ${t.name} (אזור הפגישה)` : t.name,
                                        value: t.id
                                    }))
                                ];
                                data.selectedTerritoryId = territory.id;

                                // Load technicians for territory
                                await loadTechniciansForTerritory(data, territory.id);
                            }
                        }
                    } catch (e) {
                        console.warn('[DynamicLWC] Failed to load territory:', e);
                    }
                }
            } catch (e) {
                console.warn('[DynamicLWC] Failed to check current assignment:', e);
                data.isAlreadyScheduled = false;
                data.showFiltersSection = true;
                data.showEmptyState = true;
            }
        } else {
            // No recordId - show filters section
            data.isAlreadyScheduled = false;
            data.showFiltersSection = true;
            data.showTechnicianTable = false;
            data.showEmptyState = true;
        }

        // Filter options
        data.resourceTypeOptions = [
            { label: 'כל הסוגים', value: '' },
            { label: 'טכנאי שירות', value: 'טכנאי שירות' },
            { label: 'טכנאי מכירה', value: 'טכנאי מכירה' }
        ];
        data.selectedResourceType = '';
        data.selectedTechnicianFilter = '';
        data.technicianFilterOptions = [{ label: 'כל הטכנאים', value: '' }];

        // Modal states
        data.showAssignModal = false;
        data.showConfirmModal = false;
        data.showApprovalReasonModal = false;
        data.showManagerApprovalModal = false;
        data.showManagerSelectionModal = false;
        data.showTerritoryMismatchModal = false;
        data.showDayScheduleModal = false;
        data.toasts = [];

        // Manager options for approval
        data.managerOptions = [
            { label: 'איילת', value: 'איילת' },
            { label: 'שיראל', value: 'שיראל' }
        ];

        console.log('[DynamicLWC] TechnicianSelector data ready:', {
            parentTerritories: data.parentTerritories.length,
            childTerritories: data.childTerritories.length,
            technicians: data.technicians.length,
            isAlreadyScheduled: data.isAlreadyScheduled,
            showFiltersSection: data.showFiltersSection,
            showTechnicianTable: data.showTechnicianTable
        });

    } catch (e) {
        console.error('[DynamicLWC] Error loading technician selector data:', e);
        data.isLoading = false;
        data.isAlreadyScheduled = false;
        data.showFiltersSection = true;
        data.showEmptyState = true;
    }
}

/**
 * Load technicians with availability for a territory
 */
async function loadTechniciansForTerritory(data, territoryId) {
    if (!territoryId || territoryId === 'ALL') {
        data.technicians = [];
        data.showTechnicianTable = false;
        data.showEmptyState = true;
        return;
    }

    try {
        const technicians = await callApex('TechnicianSelectorService', 'getTechniciansWithAvailability', {
            territoryId: territoryId
        });
        console.log('[DynamicLWC] Got technicians:', technicians?.length);

        if (technicians && technicians.length > 0) {
            // Process technicians for display
            data.technicians = technicians.map(tech => ({
                ...tech,
                displayKey: tech.id,
                territoryClass: tech.territoryId === data.originalTerritoryId ?
                    'tech-territory original-territory' : 'tech-territory',
                days: (tech.days || []).map(day => ({
                    ...day,
                    key: `${tech.id}-${day.dayDate}`,
                    cellClass: getDayCellClass(day),
                    statusBadgeClass: getStatusBadgeClass(day),
                    statusText: getStatusText(day),
                    shortDate: day.dayDisplay ? day.dayDisplay.split(' - ')[0] : '',
                    progressStyle: `width: ${day.maxAppointments > 0 ?
                        Math.round((day.currentAppointments / day.maxAppointments) * 100) : 0}%;
                        background: ${day.currentAppointments >= day.maxAppointments ? '#f44336' : '#4caf50'}`
                }))
            }));

            // Build day headers from first technician with days
            const firstTechWithDays = data.technicians.find(t => t.days && t.days.length > 0);
            if (firstTechWithDays) {
                data.dayHeaders = firstTechWithDays.days.map(day => ({
                    key: `header-${day.dayDate}`,
                    dayDate: day.dayDate,
                    dayName: day.dayName,
                    dateDisplay: day.dayDisplay ? day.dayDisplay.split(' - ')[0] : '',
                    headerClass: `day-header${day.isToday ? ' today' : ''}${day.isWeekend ? ' weekend' : ''}`
                }));
            }

            // Build technician filter options
            const uniqueTechs = new Map();
            data.technicians.forEach(tech => {
                if (!uniqueTechs.has(tech.id)) {
                    uniqueTechs.set(tech.id, tech);
                }
            });
            data.technicianFilterOptions = [
                { label: 'כל הטכנאים', value: '' },
                ...Array.from(uniqueTechs.values()).map(tech => ({
                    label: tech.territoryId === data.originalTerritoryId ? `✓ ${tech.name}` : tech.name,
                    value: tech.id
                }))
            ];

            data.allTechnicians = [...data.technicians];
            data.showTechnicianTable = true;
            data.showEmptyState = false;
            data.hasTechnicians = true;
        } else {
            data.technicians = [];
            data.allTechnicians = [];
            data.showTechnicianTable = false;
            data.showEmptyState = true;
            data.hasTechnicians = false;
        }
    } catch (e) {
        console.warn('[DynamicLWC] Failed to load technicians:', e);
        data.technicians = [];
        data.showTechnicianTable = false;
        data.showEmptyState = true;
    }
}

/**
 * Get CSS class for day cell based on status
 */
function getDayCellClass(day) {
    let className = 'day-cell';

    if (day.notWorkingInTerritory) {
        return className + ' not-working';
    }
    if (day.isAbsent) {
        return className + ' absent';
    }
    if (day.noCapacityDefined) {
        return className + ' no-capacity';
    }
    if (day.isWeekend) {
        className += ' weekend';
    }
    if (day.currentAppointments >= day.maxAppointments) {
        className += ' full';
    } else if (day.currentAppointments > 0) {
        className += ' partial';
    } else {
        className += ' available';
    }
    if (day.isToday) {
        className += ' today';
    }

    return className;
}

/**
 * Get status badge class
 */
function getStatusBadgeClass(day) {
    let className = 'status-badge';

    if (day.notWorkingInTerritory) return className + ' not-working';
    if (day.isAbsent) return className + ' absent';
    if (day.noCapacityDefined) return className + ' no-capacity';
    if (day.currentAppointments >= day.maxAppointments) return className + ' full';
    if (day.currentAppointments > 0) return className + ' partial';
    return className + ' available';
}

/**
 * Get status text for day cell
 */
function getStatusText(day) {
    if (day.notWorkingInTerritory) return '-';
    if (day.isAbsent) return 'בחופש';
    if (day.noCapacityDefined) return 'אין קיבולת';
    if (day.currentAppointments >= day.maxAppointments) return 'מלא';
    return `${day.availableSlots || 0} פנויים`;
}

/**
 * Load data for Inventory Setup component - GENERIC PATTERN
 * This demonstrates how to load data and compute derived properties
 */
async function loadInventorySetupData(data) {
    console.log('[DynamicLWC] Loading InventorySetup data...');

    try {
        // Call the Apex method to get zones with bins
        const zones = await callApex('InventoryManagementController', 'getZonesWithBins', {});
        console.log('[DynamicLWC] Got zones:', zones?.length);

        data.zones = (zones || []).map((zone, index) => ({
            ...zone,
            tabClass: index === 0 ? 'zone-tab active' : 'zone-tab'
        }));

        // Set selected zone
        if (data.zones.length > 0) {
            data.selectedZoneId = data.zones[0].zoneId;
            data.selectedZone = data.zones[0];

            // Build grid data for the selected zone
            buildGridDataForZone(data);
        }

        // Compute totals (mimicking the getter functions from the LWC)
        computeInventoryTotals(data);

        data.isLoading = false;
        data.showBinModal = false;
        data.selectedBin = null;

    } catch (e) {
        console.error('[DynamicLWC] Error loading inventory data:', e);
        data.zones = [];
        data.isLoading = false;
    }
}

/**
 * Build grid data structure for warehouse visualization
 */
function buildGridDataForZone(data) {
    const zone = data.selectedZone;
    if (!zone) {
        data.gridRows = [];
        return;
    }

    const maxCol = zone.maxColumn || 3;
    const maxLevel = zone.maxLevel || 5;

    if (maxCol === 0 || maxLevel === 0) {
        data.gridRows = [];
        data.noGridData = true;
        return;
    }

    // Build by COLUMNS - each column has R1 and R2 side by side
    const columns = [];
    for (let col = 1; col <= maxCol; col++) {
        const levels = [];
        for (let lvl = maxLevel; lvl >= 1; lvl--) {
            const binR1 = getBinForCell(zone.bins, 'R1', col, lvl);
            const binR2 = getBinForCell(zone.bins, 'R2', col, lvl);

            levels.push({
                value: lvl,
                column: col,
                r1: buildBinCellData(binR1, 'R1'),
                r2: buildBinCellData(binR2, 'R2')
            });
        }
        columns.push({
            value: col,
            levels: levels
        });
    }

    data.gridRows = columns;
    data.noGridData = columns.length === 0;
}

function getBinForCell(bins, row, column, level) {
    if (!bins) return null;
    return bins.find(b => b.row === row && b.column === column && b.level === level);
}

function buildBinCellData(bin, row) {
    if (!bin) {
        return {
            row: row,
            hasBin: false,
            binId: null,
            isEmpty: true,
            capacityDisplay: '0/18',
            binClass: 'bin-cell not-exists',
            firstProduct: null
        };
    }

    let firstProduct = null;
    if (bin.inventory && bin.inventory.length > 0) {
        const prod = bin.inventory[0];
        firstProduct = {
            productCode: prod.productCode,
            isSerialized: prod.isSerialized === true,
            typeIndicator: prod.isSerialized === true ? 'S' : 'Q',
            typeClass: prod.isSerialized === true ? 'type-indicator serialized' : 'type-indicator non-serialized'
        };
    }

    // Determine bin class based on fill status
    let binClass = 'bin-cell empty';
    if (bin.isEmpty) {
        binClass = 'bin-cell empty';
    } else if (bin.currentQuantity >= bin.maxCapacity) {
        binClass = 'bin-cell full';
    } else {
        binClass = 'bin-cell partial';
    }

    return {
        row: row,
        hasBin: true,
        binId: bin.binId,
        isEmpty: bin.isEmpty,
        capacityDisplay: `${bin.currentQuantity || 0}/${bin.maxCapacity || 18}`,
        binClass: binClass,
        firstProduct: firstProduct
    };
}

/**
 * Compute inventory totals - mimics computed getters from LWC
 */
function computeInventoryTotals(data) {
    let totalOccupied = 0;
    let totalCapacity = 0;

    if (data.zones) {
        data.zones.forEach(zone => {
            if (zone.bins) {
                zone.bins.forEach(bin => {
                    totalOccupied += bin.currentQuantity || 0;
                    totalCapacity += bin.maxCapacity || 0;
                });
            }
        });
    }

    const totalAvailable = totalCapacity - totalOccupied;
    const occupancyPercentage = totalCapacity > 0 ? Math.round((totalOccupied / totalCapacity) * 100) : 0;

    // Set computed values with formatting
    data.totalOccupied = totalOccupied.toLocaleString();
    data.totalCapacity = totalCapacity.toLocaleString();
    data.totalAvailable = totalAvailable.toLocaleString();
    data.occupancyPercentage = occupancyPercentage;
}

/**
 * Build route groups from appointments for display
 */
function buildRouteGroups(appointments, state) {
    if (!appointments || appointments.length === 0) {
        return [];
    }

    // Build technician lookup from state if available
    const technicianLookup = {};
    if (state.technicianOptions && Array.isArray(state.technicianOptions)) {
        state.technicianOptions.forEach(opt => {
            if (opt.value && opt.label) {
                technicianLookup[opt.value] = opt.label;
            }
        });
    }

    // Group appointments by technician
    const technicianMap = {};

    appointments.forEach((appt, index) => {
        const techId = appt.Service_Resource__c || appt.ServiceResource?.Id || appt.ServiceResourceId || 'unassigned';
        // Look up technician name - prioritize TechnicianName from backend, then lookup, then fallback
        const techName = appt.TechnicianName ||
                        technicianLookup[techId] ||
                        appt.ServiceResource?.Name ||
                        (state.selectedTechnicianId && state.selectedTechnicianId === techId ?
                            (state.technicianOptions?.find(t => t.value === techId)?.label || 'טכנאי') :
                            'טכנאי');

        if (!technicianMap[techId]) {
            technicianMap[techId] = {
                technicianId: techId,
                technicianName: techName,
                appointments: []
            };
        }
        technicianMap[techId].appointments.push(appt);
    });

    // Convert to array and build display routes
    return Object.values(technicianMap).map(group => {
        const legDetails = group.appointments.map((appt, idx) => ({
            appointmentId: appt.Id,
            appointmentNumber: appt.AppointmentNumber || appt.Name || `SA-${idx + 1}`,
            appointmentNumberUrl: `#/ServiceAppointment/${appt.Id}`,
            order: idx + 1,
            accountName: appt.Account?.Name || appt.AccountName || '',
            appointmentTime: appt.SchedStartTime ?
                new Date(appt.SchedStartTime).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' }) : '-',
            destinationAddress: `${appt.Street || ''} ${appt.City || ''}`.trim() || 'לא צוינה כתובת',
            addressClass: (appt.Latitude && appt.Longitude) ? '' : 'slds-text-color_error',
            latitude: appt.Latitude || null,
            longitude: appt.Longitude || null,
            distance: '-',
            travelTime: '-',
            isPinned: false,
            isStartPinned: false,
            isEndPinned: false,
            pinnedClass: '',
            pinnedText: '',
            draggable: 'true',
            dragTitle: 'גרור לשינוי סדר'
        }));

        // Create map markers for display
        const mapMarkers = group.appointments
            .filter(appt => appt.Latitude && appt.Longitude)
            .map((appt, idx) => ({
                location: {
                    Latitude: parseFloat(appt.Latitude),
                    Longitude: parseFloat(appt.Longitude)
                },
                title: appt.AppointmentNumber || `#${idx + 1}`,
                description: `${appt.Street || ''} ${appt.City || ''}`.trim(),
                accountName: appt.Account?.Name || appt.AccountName || '',
                value: String(idx + 1)
            }));

        // Calculate map center
        let mapCenter = { Latitude: 32.0853, Longitude: 34.7818 }; // Default Tel Aviv
        if (mapMarkers.length > 0) {
            const avgLat = mapMarkers.reduce((sum, m) => sum + m.location.Latitude, 0) / mapMarkers.length;
            const avgLng = mapMarkers.reduce((sum, m) => sum + m.location.Longitude, 0) / mapMarkers.length;
            mapCenter = { Latitude: avgLat, Longitude: avgLng };
        }

        return {
            technicianId: group.technicianId,
            technicianName: group.technicianName,
            displayRoute: {
                legDetails,
                mapMarkers: mapMarkers.length > 0 ? mapMarkers : null,
                mapCenter,
                hasScheduleChanges: false,
                scheduleChanges: []
            }
        };
    });
}

/**
 * DynamicComponentRenderer - Main component that loads and renders dynamic components
 */
export function DynamicComponentRenderer({
    componentName,
    recordId,
    objectName,
    modalWidth = 'medium',
    modalHeight = 'auto',
    onClose,
    ...additionalProps
}) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [componentData, setComponentData] = useState(null);
    const [componentState, setComponentState] = useState({});
    const containerRef = useRef(null);
    const styleRef = useRef(null);

    // Ref to always have access to latest state in handlers
    const stateRef = useRef(componentState);
    stateRef.current = componentState;

    // Load component data from backend
    useEffect(() => {
        const loadComponent = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`${API_BASE}/api/component/${componentName}`);
                if (!response.ok) {
                    throw new Error(`Component ${componentName} not found`);
                }

                const data = await response.json();

                // Parse the response - API returns { name, path, files: { filename: { content, size } } }
                let htmlContent = '';
                let cssContent = '';
                let jsContent = '';

                if (data.files) {
                    // New format with files object
                    Object.entries(data.files).forEach(([filename, fileData]) => {
                        const content = fileData.content || fileData;
                        if (filename.endsWith('.html')) {
                            htmlContent = content;
                        } else if (filename.endsWith('.css')) {
                            cssContent = content;
                        } else if (filename.endsWith('.js')) {
                            jsContent = content;
                        }
                    });
                } else {
                    // Legacy format
                    htmlContent = data.html || '';
                    cssContent = data.css || '';
                    jsContent = data.js || '';
                }

                const processedData = {
                    name: componentName,
                    html: htmlContent,
                    css: cssContent,
                    js: jsContent
                };

                setComponentData(processedData);

                // Initialize state
                const initialState = {
                    recordId: recordId,
                    objectApiName: objectName,
                    loading: true,
                    ...additionalProps
                };

                // Load component-specific data
                const loadedState = await loadComponentData(componentName, jsContent, htmlContent, initialState);
                setComponentState(loadedState);

                // Inject CSS
                if (cssContent) {
                    injectStyles(cssContent, componentName);
                }

            } catch (err) {
                console.error('[DynamicLWC] Error loading component:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadComponent();

        // Cleanup styles on unmount
        return () => {
            if (styleRef.current) {
                styleRef.current.remove();
            }
        };
    }, [componentName, recordId, objectName]);

    // Inject CSS styles
    const injectStyles = useCallback((css, compName) => {
        if (styleRef.current) {
            styleRef.current.remove();
        }

        const styleEl = document.createElement('style');
        styleEl.id = `lwc-style-${compName}`;

        // Scope CSS selectors
        const scopedCss = css.replace(/([^\r\n,{}]+)(,(?=[^}]*{)|\s*{)/g, (match, selector, suffix) => {
            const trimmed = selector.trim();
            if (trimmed.startsWith('@') || trimmed.startsWith(':host') ||
                trimmed.includes(`.lwc-${compName}`) || trimmed === '') {
                return match;
            }
            // Convert :host to component class
            if (trimmed === ':host') {
                return `.lwc-${compName}${suffix}`;
            }
            return `.lwc-${compName} ${trimmed}${suffix}`;
        });

        styleEl.textContent = scopedCss;
        document.head.appendChild(styleEl);
        styleRef.current = styleEl;
    }, []);

    // Helper functions for technician selector cell styling
    const getDayCellClassHelper = (day) => {
        let className = 'day-cell';
        if (day.notWorkingInTerritory) return className + ' not-working';
        if (day.isAbsent) return className + ' absent';
        if (day.noCapacityDefined) return className + ' no-capacity';
        if (day.isWeekend) className += ' weekend';
        if (day.currentAppointments >= day.maxAppointments) className += ' full';
        else if (day.currentAppointments > 0) className += ' partial';
        else className += ' available';
        if (day.isToday) className += ' today';
        return className;
    };

    const getStatusBadgeClassHelper = (day) => {
        let className = 'status-badge';
        if (day.notWorkingInTerritory) return className + ' not-working';
        if (day.isAbsent) return className + ' absent';
        if (day.noCapacityDefined) return className + ' no-capacity';
        if (day.currentAppointments >= day.maxAppointments) return className + ' full';
        if (day.currentAppointments > 0) return className + ' partial';
        return className + ' available';
    };

    const getStatusTextHelper = (day) => {
        if (day.notWorkingInTerritory) return '-';
        if (day.isAbsent) return 'בחופש';
        if (day.noCapacityDefined) return 'אין קיבולת';
        if (day.currentAppointments >= day.maxAppointments) return 'מלא';
        return `${day.availableSlots || 0} פנויים`;
    };

    // Create event handlers
    const createHandlers = useCallback(() => {
        const handlers = {};

        handlers.handleClose = () => {
            if (onClose) onClose();
        };

        handlers.handleChange = (event) => {
            const target = event?.target;
            if (target && target.name) {
                setComponentState(prev => ({
                    ...prev,
                    [target.name]: target.type === 'checkbox' ? target.checked : target.value
                }));
            }
        };

        handlers.handleGenericEvent = (event) => {
            console.log('[DynamicLWC] Generic event:', event);
        };

        // ShowToastEvent handler - display toast notifications
        handlers.showToast = (event) => {
            const detail = event?.detail || event || {};
            const title = detail.title || 'הודעה';
            const message = detail.message || '';
            const variant = detail.variant || 'info'; // info, success, warning, error

            // Use browser notification or create a toast element
            console.log(`[Toast] ${variant}: ${title} - ${message}`);

            // Create toast element
            const toastContainer = document.getElementById('lwc-toast-container') || (() => {
                const container = document.createElement('div');
                container.id = 'lwc-toast-container';
                container.style.cssText = 'position:fixed;top:16px;right:16px;z-index:10000;max-width:400px;';
                document.body.appendChild(container);
                return container;
            })();

            const variantStyles = {
                success: 'background:#04844b;color:white;',
                error: 'background:#c23934;color:white;',
                warning: 'background:#ff9800;color:white;',
                info: 'background:#0070d2;color:white;'
            };

            const toast = document.createElement('div');
            toast.style.cssText = `${variantStyles[variant] || variantStyles.info}padding:12px 16px;border-radius:4px;margin-bottom:8px;box-shadow:0 2px 8px rgba(0,0,0,0.2);animation:slideIn 0.3s ease;`;
            toast.innerHTML = `<strong style="display:block;margin-bottom:4px;">${title}</strong><span>${message}</span>`;
            toastContainer.appendChild(toast);

            // Auto-remove after 5 seconds
            setTimeout(() => {
                toast.style.animation = 'slideOut 0.3s ease';
                setTimeout(() => toast.remove(), 300);
            }, 5000);
        };

        // CloseActionScreenEvent handler - close the modal/quick action
        handlers.closeAction = () => {
            console.log('[DynamicLWC] CloseActionScreenEvent dispatched');
            if (onClose) onClose();
        };

        // Generic dispatchEvent handler for LWC components
        handlers.dispatchEvent = (event) => {
            const eventType = event?.type || event?.constructor?.name || 'CustomEvent';
            console.log(`[DynamicLWC] dispatchEvent: ${eventType}`, event);

            if (eventType === 'ShowToastEvent' || event?.detail?.title) {
                handlers.showToast(event);
            } else if (eventType === 'CloseActionScreenEvent') {
                handlers.closeAction();
            }
        };

        // Technician type change handler - filters technicians by type locally
        handlers.handleTechnicianTypeChange = (event) => {
            const newType = event?.target?.value;
            console.log('[DynamicLWC] handleTechnicianTypeChange:', newType);

            setComponentState(prev => {
                // Filter from allTechnicians based on selected type
                const allTechs = prev.allTechnicians || [];
                let filteredTechs = allTechs;

                if (newType && newType !== 'כל סוגי הטכנאים') {
                    filteredTechs = allTechs.filter(t =>
                        (t.Technical_Type__c || t.ResourceType) === newType
                    );
                }

                const options = [{ label: 'כל הטכנאים', value: 'all' }];
                options.push(...filteredTechs.map(t => ({
                    label: t.Name,
                    value: t.Id,
                    type: t.Technical_Type__c || t.ResourceType
                })));

                return {
                    ...prev,
                    selectedTechnicianType: newType,
                    technicianType: newType,  // alias
                    technicianOptions: options,
                    selectedTechnician: 'all',
                    selectedTechnicianId: 'all'  // alias
                };
            });
        };

        handlers.handleTechnicianChange = (event) => {
            const newTechnician = event?.target?.value;
            console.log('[DynamicLWC] handleTechnicianChange:', newTechnician);
            setComponentState(prev => ({
                ...prev,
                selectedTechnician: newTechnician,
                selectedTechnicianId: newTechnician  // alias
            }));
        };

        handlers.handleDateChange = (event) => {
            const newDate = event?.target?.value;
            setComponentState(prev => ({ ...prev, scheduleDate: newDate }));
        };

        handlers.handleOptimizeRoutes = async () => {
            console.log('[DynamicLWC] handleCalculateClick/handleOptimizeRoutes called');
            const currentState = stateRef.current;
            console.log('[DynamicLWC] Current state:', {
                selectedTechnician: currentState.selectedTechnician,
                scheduleDate: currentState.scheduleDate,
                selectedTechnicianType: currentState.selectedTechnicianType
            });

            setComponentState(prev => ({ ...prev, isOptimizing: true, loading: true, error: null }));

            try {
                const technicianId = currentState.selectedTechnician === 'all' ? null : currentState.selectedTechnician;
                const scheduleDate = currentState.scheduleDate;

                console.log('[DynamicLWC] Calling getAppointments with:', { technicianId, scheduleDate });

                const result = await callApex('TechnicianRouteController', 'getAppointments', {
                    technicianId: technicianId,
                    scheduleDate: scheduleDate
                });

                console.log('[DynamicLWC] Got appointments:', result?.length || 0);
                console.log('[DynamicLWC] First appointment:', result?.[0]);

                // Build route groups for display
                const routeGroups = buildRouteGroups(result || [], currentState);
                console.log('[DynamicLWC] Built routeGroups:', routeGroups.length, routeGroups);

                setComponentState(prev => {
                    const newState = {
                        ...prev,
                        appointments: result || [],
                        technicianRoutes: routeGroups,
                        hasRoutes: routeGroups.length > 0,
                        isOptimizing: false,
                        loading: false,
                        showResults: true
                    };
                    console.log('[DynamicLWC] New state hasRoutes:', newState.hasRoutes);
                    return newState;
                });
            } catch (e) {
                console.error('[DynamicLWC] Failed to optimize:', e);
                setComponentState(prev => ({ ...prev, isOptimizing: false, loading: false, error: e.message }));
            }
        };

        // Alias for handleCalculateClick - matches LWC template
        handlers.handleCalculateClick = handlers.handleOptimizeRoutes;

        // Open address audit modal
        handlers.openAddressAudit = async () => {
            console.log('[DynamicLWC] openAddressAudit called');
            const currentState = stateRef.current;
            setComponentState(prev => ({ ...prev, auditOpen: true, auditLoading: true, auditRows: [] }));

            try {
                // Get appointments for the selected technician and date
                let appointments = currentState.appointments || [];

                if (appointments.length === 0) {
                    // First load appointments if not loaded
                    const result = await callApex('TechnicianRouteController', 'getAppointments', {
                        technicianId: currentState.selectedTechnician === 'all' ? null : currentState.selectedTechnician,
                        scheduleDate: currentState.scheduleDate
                    });
                    appointments = result || [];
                    setComponentState(prev => ({ ...prev, appointments }));
                }

                // For now, just show the appointments with their addresses
                const auditRows = appointments.map((appt, idx) => ({
                    appointmentId: appt.Id,
                    appointmentNumber: appt.AppointmentNumber || appt.Name || `#${idx + 1}`,
                    address: `${appt.Street || ''} ${appt.City || ''}`.trim() || 'לא צוינה כתובת',
                    savedLat: appt.Latitude || '-',
                    savedLng: appt.Longitude || '-',
                    googleLat: '-',
                    googleLng: '-',
                    distanceKm: '-',
                    rowClass: '',
                    isProblem: false,
                    displayIndex: idx + 1,
                    recordUrl: `#/appointment/${appt.Id}`
                }));

                setComponentState(prev => ({ ...prev, auditRows, auditLoading: false }));
            } catch (e) {
                console.error('[DynamicLWC] Address audit error:', e);
                setComponentState(prev => ({ ...prev, auditLoading: false, error: e.message }));
            }
        };

        // Close address audit modal
        handlers.closeAddressAudit = () => {
            setComponentState(prev => ({ ...prev, auditOpen: false, auditRows: [] }));
        };

        // Confirm routes
        handlers.confirmRoutes = async () => {
            console.log('[DynamicLWC] confirmRoutes called');
            alert('אישור מסלול - פונקציונליות זו תיושם בהמשך');
        };

        // Handle coordinate update
        handlers.handleUpdateCoord = (event) => {
            const appointmentId = event.target?.dataset?.id;
            console.log('[DynamicLWC] handleUpdateCoord for:', appointmentId);
            alert(`עדכון קואורדינטות עבור פגישה ${appointmentId} - פונקציונליות זו תיושם בהמשך`);
        };

        // Pin as start
        handlers.pinAsStart = (event) => {
            const appointmentId = event.target?.dataset?.id || event.currentTarget?.dataset?.id;
            const technicianId = event.target?.dataset?.technicianId || event.currentTarget?.dataset?.technicianId;
            console.log('[DynamicLWC] pinAsStart:', appointmentId, technicianId);
            // TODO: Implement pin as start logic
        };

        // Pin as end
        handlers.pinAsEnd = (event) => {
            const appointmentId = event.target?.dataset?.id || event.currentTarget?.dataset?.id;
            const technicianId = event.target?.dataset?.technicianId || event.currentTarget?.dataset?.technicianId;
            console.log('[DynamicLWC] pinAsEnd:', appointmentId, technicianId);
            // TODO: Implement pin as end logic
        };

        // Unpin start
        handlers.unpinStart = (event) => {
            const appointmentId = event.target?.dataset?.id || event.currentTarget?.dataset?.id;
            console.log('[DynamicLWC] unpinStart:', appointmentId);
            // TODO: Implement unpin start logic
        };

        // Unpin end
        handlers.unpinEnd = (event) => {
            const appointmentId = event.target?.dataset?.id || event.currentTarget?.dataset?.id;
            console.log('[DynamicLWC] unpinEnd:', appointmentId);
            // TODO: Implement unpin end logic
        };

        // Open suggest modal
        handlers.openSuggestModal = (event) => {
            const appointmentId = event.target?.dataset?.id || event.currentTarget?.dataset?.id;
            console.log('[DynamicLWC] openSuggestModal:', appointmentId);
            setComponentState(prev => ({
                ...prev,
                showSuggestModal: true,
                suggestForAppointment: prev.appointments?.find(a => a.Id === appointmentId) || null,
                suggestLoading: false,
                addressSuggestions: [],
                hasAddressSuggestions: false
            }));
        };

        // Close suggest modal
        handlers.closeSuggestModal = () => {
            setComponentState(prev => ({
                ...prev,
                showSuggestModal: false,
                suggestForAppointment: null,
                addressSuggestions: []
            }));
        };

        // Confirm original address
        handlers.confirmOriginalAddress = () => {
            console.log('[DynamicLWC] confirmOriginalAddress');
            handlers.closeSuggestModal();
        };

        // Apply suggested address
        handlers.applySuggestedAddress = (event) => {
            const index = event.target?.dataset?.index || event.currentTarget?.dataset?.index;
            console.log('[DynamicLWC] applySuggestedAddress index:', index);
            // TODO: Implement apply suggested address logic
            handlers.closeSuggestModal();
        };

        // Drag and drop handlers (placeholders)
        handlers.handleRowDragStart = (event) => {
            console.log('[DynamicLWC] handleRowDragStart');
        };
        handlers.handleRowDragOver = (event) => {
            event.preventDefault();
        };
        handlers.handleRowDrop = (event) => {
            console.log('[DynamicLWC] handleRowDrop');
        };
        handlers.handleRowDragEnd = (event) => {
            console.log('[DynamicLWC] handleRowDragEnd');
        };

        // ========================================
        // TechnicianSelector specific handlers
        // ========================================

        // Parent territory change - loads child territories
        handlers.handleParentTerritoryChange = async (event) => {
            const parentId = event?.detail?.value || event?.target?.value;
            console.log('[DynamicLWC] handleParentTerritoryChange:', parentId);

            if (!parentId || parentId === '') {
                setComponentState(prev => ({
                    ...prev,
                    selectedParentTerritoryId: '',
                    childTerritories: [],
                    childTerritoryOptions: [],
                    selectedTerritoryId: '',
                    technicians: [],
                    showTechnicianTable: false,
                    showEmptyState: true
                }));
                return;
            }

            setComponentState(prev => ({ ...prev, selectedParentTerritoryId: parentId, isLoading: true }));

            try {
                const children = await callApex('TechnicianSelectorService', 'getChildTerritories', {
                    parentTerritoryId: parentId
                });
                console.log('[DynamicLWC] Got child territories:', children?.length);

                const currentState = stateRef.current;
                const options = [
                    { label: 'בחר אזור שירות...', value: '' },
                    ...children.map(t => ({
                        label: t.id === currentState.originalTerritoryId ? `✓ ${t.name} (אזור הפגישה)` : t.name,
                        value: t.id
                    }))
                ];

                setComponentState(prev => ({
                    ...prev,
                    childTerritories: children || [],
                    childTerritoryOptions: options,
                    selectedTerritoryId: '',
                    technicians: [],
                    showTechnicianTable: false,
                    showEmptyState: true,
                    isLoading: false
                }));
            } catch (e) {
                console.error('[DynamicLWC] Error loading child territories:', e);
                setComponentState(prev => ({ ...prev, isLoading: false }));
            }
        };

        // Child territory change - loads technicians
        handlers.handleTerritoryChange = async (event) => {
            const territoryId = event?.detail?.value || event?.target?.value;
            console.log('[DynamicLWC] handleTerritoryChange:', territoryId);

            if (!territoryId || territoryId === '') {
                setComponentState(prev => ({
                    ...prev,
                    selectedTerritoryId: '',
                    technicians: [],
                    allTechnicians: [],
                    showTechnicianTable: false,
                    showEmptyState: true
                }));
                return;
            }

            setComponentState(prev => ({ ...prev, selectedTerritoryId: territoryId, isLoading: true }));

            try {
                const technicians = await callApex('TechnicianSelectorService', 'getTechniciansWithAvailability', {
                    territoryId: territoryId
                });
                console.log('[DynamicLWC] Got technicians:', technicians?.length);

                if (technicians && technicians.length > 0) {
                    const currentState = stateRef.current;

                    // Process technicians for display
                    const processedTechs = technicians.map(tech => ({
                        ...tech,
                        displayKey: tech.id,
                        territoryClass: tech.territoryId === currentState.originalTerritoryId ?
                            'tech-territory original-territory' : 'tech-territory',
                        days: (tech.days || []).map(day => ({
                            ...day,
                            key: `${tech.id}-${day.dayDate}`,
                            cellClass: getDayCellClassHelper(day),
                            statusBadgeClass: getStatusBadgeClassHelper(day),
                            statusText: getStatusTextHelper(day),
                            shortDate: day.dayDisplay ? day.dayDisplay.split(' - ')[0] : '',
                            progressStyle: `width: ${day.maxAppointments > 0 ?
                                Math.round((day.currentAppointments / day.maxAppointments) * 100) : 0}%;
                                background: ${day.currentAppointments >= day.maxAppointments ? '#f44336' : '#4caf50'}`
                        }))
                    }));

                    // Build day headers
                    const firstTechWithDays = processedTechs.find(t => t.days && t.days.length > 0);
                    const dayHeaders = firstTechWithDays ? firstTechWithDays.days.map(day => ({
                        key: `header-${day.dayDate}`,
                        dayDate: day.dayDate,
                        dayName: day.dayName,
                        dateDisplay: day.dayDisplay ? day.dayDisplay.split(' - ')[0] : '',
                        headerClass: `day-header${day.isToday ? ' today' : ''}${day.isWeekend ? ' weekend' : ''}`
                    })) : [];

                    // Build technician filter options
                    const techFilterOptions = [
                        { label: 'כל הטכנאים', value: '' },
                        ...processedTechs.map(tech => ({
                            label: tech.territoryId === currentState.originalTerritoryId ? `✓ ${tech.name}` : tech.name,
                            value: tech.id
                        }))
                    ];

                    setComponentState(prev => ({
                        ...prev,
                        technicians: processedTechs,
                        allTechnicians: processedTechs,
                        dayHeaders: dayHeaders,
                        technicianFilterOptions: techFilterOptions,
                        showTechnicianTable: true,
                        showEmptyState: false,
                        hasTechnicians: true,
                        isLoading: false
                    }));
                } else {
                    setComponentState(prev => ({
                        ...prev,
                        technicians: [],
                        allTechnicians: [],
                        showTechnicianTable: false,
                        showEmptyState: true,
                        hasTechnicians: false,
                        isLoading: false
                    }));
                }
            } catch (e) {
                console.error('[DynamicLWC] Error loading technicians:', e);
                setComponentState(prev => ({ ...prev, isLoading: false }));
            }
        };

        // Resource type filter change
        handlers.handleResourceTypeChange = (event) => {
            const resourceType = event?.detail?.value || event?.target?.value;
            console.log('[DynamicLWC] handleResourceTypeChange:', resourceType);

            setComponentState(prev => {
                const allTechs = prev.allTechnicians || [];
                let filtered = allTechs;

                if (resourceType && resourceType !== '' && resourceType !== 'כל הסוגים') {
                    filtered = allTechs.filter(t => t.technicalType === resourceType);
                }

                // Also apply technician filter if set
                const techFilter = prev.selectedTechnicianFilter;
                if (techFilter && techFilter !== '') {
                    filtered = filtered.filter(t => t.id === techFilter);
                }

                return {
                    ...prev,
                    selectedResourceType: resourceType,
                    technicians: filtered,
                    showTechnicianTable: filtered.length > 0,
                    showEmptyState: filtered.length === 0
                };
            });
        };

        // Technician filter change
        handlers.handleTechnicianFilterChange = (event) => {
            const techId = event?.detail?.value || event?.target?.value;
            console.log('[DynamicLWC] handleTechnicianFilterChange:', techId);

            setComponentState(prev => {
                const allTechs = prev.allTechnicians || [];
                let filtered = allTechs;

                // Apply resource type filter first
                const resourceType = prev.selectedResourceType;
                if (resourceType && resourceType !== '' && resourceType !== 'כל הסוגים') {
                    filtered = filtered.filter(t => t.technicalType === resourceType);
                }

                // Then apply technician filter
                if (techId && techId !== '') {
                    filtered = filtered.filter(t => t.id === techId);
                }

                return {
                    ...prev,
                    selectedTechnicianFilter: techId,
                    technicians: filtered,
                    showTechnicianTable: filtered.length > 0,
                    showEmptyState: filtered.length === 0
                };
            });
        };

        // Refresh data
        handlers.refreshData = async () => {
            console.log('[DynamicLWC] refreshData called');
            const currentState = stateRef.current;

            if (currentState.selectedTerritoryId) {
                // Reload technicians for current territory
                await handlers.handleTerritoryChange({ detail: { value: currentState.selectedTerritoryId } });
            }
        };

        // Cell click - open assign modal (legacy handler name)
        handlers.handleCellClick = (event) => {
            const techId = event?.currentTarget?.dataset?.technicianId;
            const techName = event?.currentTarget?.dataset?.technicianName;
            const dayDate = event?.currentTarget?.dataset?.dayDate;
            console.log('[DynamicLWC] handleCellClick:', { techId, techName, dayDate });

            setComponentState(prev => ({
                ...prev,
                showAssignModal: true,
                selectedTechnicianForAssign: { id: techId, name: techName },
                selectedDateForAssign: dayDate
            }));
        };

        // Day click handler - matches original LWC template data attributes
        // Template uses: data-tech-id, data-display-key, data-territory-id, data-date
        handlers.handleDayClick = async (event) => {
            const target = event?.currentTarget || event?.target;
            const techId = target?.dataset?.techId;
            const displayKey = target?.dataset?.displayKey;
            const territoryId = target?.dataset?.territoryId;
            const dateStr = target?.dataset?.date;

            console.log('[DynamicLWC] handleDayClick:', { techId, displayKey, territoryId, dateStr });

            if (!techId || !dateStr) {
                console.warn('[DynamicLWC] handleDayClick - missing techId or dateStr');
                return;
            }

            const currentState = stateRef.current;
            const technicians = currentState.technicians || [];

            // Find the technician by displayKey or id
            let tech = technicians.find(t => t.displayKey === displayKey);
            if (!tech) {
                tech = technicians.find(t => t.id === techId);
            }

            if (!tech) {
                console.warn('[DynamicLWC] handleDayClick - technician not found');
                return;
            }

            // Find the day
            const day = (tech.days || []).find(d => d.dayDate === dateStr);
            if (!day) {
                console.warn('[DynamicLWC] handleDayClick - day not found');
                return;
            }

            // Check if day is clickable
            if (day.isWeekend) {
                console.log('[DynamicLWC] handleDayClick - cannot assign on weekend (Shabbat)');
                handlers.showToast({
                    detail: {
                        title: 'לא ניתן לשבץ',
                        message: 'לא ניתן לשבץ פגישות ביום שבת',
                        variant: 'warning'
                    }
                });
                return;
            }

            if (day.isAbsent) {
                console.log('[DynamicLWC] handleDayClick - technician absent on this day');
                handlers.showToast({
                    detail: {
                        title: 'לא ניתן לשבץ',
                        message: 'הטכנאי בחופש ביום זה',
                        variant: 'warning'
                    }
                });
                return;
            }

            // Common state for all modals
            const commonState = {
                selectedTechId: techId,
                selectedTechName: tech.name,
                selectedDate: dateStr,
                selectedDayDisplay: day.dayDisplay || `${dateStr} - ${day.dayName}`,
                selectedDayCurrentCount: day.currentAppointments,
                selectedDayMaxCount: day.maxAppointments,
                selectedTerritoryId: territoryId || currentState.selectedTerritoryId,
                selectedTechTerritoryName: tech.territoryName,
                selectedTechnicianForAssign: { id: techId, name: tech.name },
                selectedDateForAssign: dateStr
            };

            // Check if day is full - might need manager approval
            if (day.currentAppointments >= day.maxAppointments) {
                console.log('[DynamicLWC] handleDayClick - day is full, requesting manager approval');
                setComponentState(prev => ({
                    ...prev,
                    ...commonState,
                    showManagerApprovalModal: true,
                    managerApprovalReason: `היום מלא (${day.currentAppointments}/${day.maxAppointments} פגישות)`
                }));
                return;
            }

            // Check for territory mismatch
            const originalTerritoryId = currentState.originalTerritoryId;
            if (originalTerritoryId && territoryId && territoryId !== originalTerritoryId) {
                console.log('[DynamicLWC] handleDayClick - territory mismatch');
                setComponentState(prev => ({
                    ...prev,
                    ...commonState,
                    showTerritoryMismatchModal: true,
                    mismatchTerritoryName: tech.territoryName
                }));
                return;
            }

            // All checks passed - show assign modal
            setComponentState(prev => ({
                ...prev,
                ...commonState,
                showAssignModal: true
            }));
        };

        // Day right-click handler - shows day schedule
        handlers.handleDayRightClick = async (event) => {
            event.preventDefault();
            const target = event?.currentTarget || event?.target;
            const techId = target?.dataset?.techId;
            const displayKey = target?.dataset?.displayKey;
            const dateStr = target?.dataset?.date;

            console.log('[DynamicLWC] handleDayRightClick:', { techId, displayKey, dateStr });

            if (!techId || !dateStr) return;

            const currentState = stateRef.current;
            const technicians = currentState.technicians || [];

            let tech = technicians.find(t => t.displayKey === displayKey);
            if (!tech) {
                tech = technicians.find(t => t.id === techId);
            }

            setComponentState(prev => ({
                ...prev,
                showDayScheduleModal: true,
                dayScheduleTechnicianId: techId,
                dayScheduleTechnicianName: tech?.name || '',
                dayScheduleDate: dateStr,
                dayScheduleLoading: true,
                dayScheduleAppointments: []
            }));

            try {
                const appointments = await callApex('TechnicianSelectorService', 'getDayAppointments', {
                    technicianId: techId,
                    targetDate: dateStr
                });
                console.log('[DynamicLWC] Got day appointments:', appointments?.length);

                setComponentState(prev => ({
                    ...prev,
                    dayScheduleAppointments: appointments || [],
                    dayScheduleLoading: false,
                    hasDayScheduleAppointments: (appointments || []).length > 0
                }));
            } catch (e) {
                console.error('[DynamicLWC] Error loading day appointments:', e);
                setComponentState(prev => ({ ...prev, dayScheduleLoading: false }));
            }
        };

        // Cell right-click - open day schedule modal
        handlers.handleCellContextMenu = async (event) => {
            event.preventDefault();
            const techId = event?.currentTarget?.dataset?.technicianId;
            const techName = event?.currentTarget?.dataset?.technicianName;
            const dayDate = event?.currentTarget?.dataset?.dayDate;
            console.log('[DynamicLWC] handleCellContextMenu:', { techId, techName, dayDate });

            setComponentState(prev => ({
                ...prev,
                showDayScheduleModal: true,
                dayScheduleTechnicianId: techId,
                dayScheduleTechnicianName: techName,
                dayScheduleDate: dayDate,
                dayScheduleLoading: true,
                dayScheduleAppointments: []
            }));

            try {
                const appointments = await callApex('TechnicianSelectorService', 'getDayAppointments', {
                    technicianId: techId,
                    targetDate: dayDate
                });
                console.log('[DynamicLWC] Got day appointments:', appointments?.length);

                setComponentState(prev => ({
                    ...prev,
                    dayScheduleAppointments: appointments || [],
                    dayScheduleLoading: false,
                    hasDayScheduleAppointments: (appointments || []).length > 0
                }));
            } catch (e) {
                console.error('[DynamicLWC] Error loading day appointments:', e);
                setComponentState(prev => ({ ...prev, dayScheduleLoading: false }));
            }
        };

        // Close modals
        handlers.handleCloseAssignModal = () => {
            setComponentState(prev => ({ ...prev, showAssignModal: false }));
        };

        handlers.handleCloseDaySchedule = () => {
            setComponentState(prev => ({ ...prev, showDayScheduleModal: false }));
        };

        handlers.handleCloseConfirmModal = () => {
            setComponentState(prev => ({ ...prev, showConfirmModal: false }));
        };

        handlers.closeTerritoryMismatchModal = () => {
            setComponentState(prev => ({ ...prev, showTerritoryMismatchModal: false }));
        };

        handlers.closeManagerApprovalModal = () => {
            setComponentState(prev => ({
                ...prev,
                showManagerApprovalModal: false,
                showAssignModal: true  // Go back to assign modal
            }));
        };

        handlers.closeApprovalReasonModal = () => {
            setComponentState(prev => ({
                ...prev,
                showApprovalReasonModal: false,
                approvalReason: '',
                showAssignModal: true  // Go back to assign modal
            }));
        };

        // Manager approval flow handlers
        handlers.handleHaveManagerApproval = () => {
            console.log('[DynamicLWC] handleHaveManagerApproval');
            setComponentState(prev => ({
                ...prev,
                showManagerApprovalModal: false,
                showManagerSelectionModal: true
            }));
        };

        handlers.closeManagerSelectionModal = () => {
            setComponentState(prev => ({
                ...prev,
                showManagerSelectionModal: false,
                showManagerApprovalModal: true
            }));
        };

        handlers.handleManagerChange = (event) => {
            const managerName = event?.detail?.value || event?.target?.value;
            console.log('[DynamicLWC] handleManagerChange:', managerName);
            setComponentState(prev => ({
                ...prev,
                selectedManagerName: managerName
            }));
        };

        handlers.submitWithManagerApproval = async () => {
            console.log('[DynamicLWC] submitWithManagerApproval');
            const currentState = stateRef.current;

            if (!currentState.selectedManagerName) {
                handlers.showToast({
                    detail: {
                        title: 'שגיאה',
                        message: 'יש לבחור מנהל מאשר',
                        variant: 'error'
                    }
                });
                return;
            }

            // Execute assignment with manager approval
            try {
                setComponentState(prev => ({ ...prev, isAssigning: true }));

                const result = await callApex('TechnicianSelectorService', 'assignAppointmentToTechnicianWithReason', {
                    serviceAppointmentId: currentState.serviceAppointmentId || recordId,
                    technicianId: currentState.selectedTechId,
                    scheduledDate: currentState.selectedDate,
                    startTime: currentState.assignmentTime || '09:00',
                    reason: `אישור מנהל: ${currentState.selectedManagerName}`
                });

                console.log('[DynamicLWC] Manager approval assignment result:', result);

                setComponentState(prev => ({
                    ...prev,
                    showManagerSelectionModal: false,
                    isAssigning: false
                }));

                if (result?.success) {
                    handlers.showToast({
                        detail: {
                            title: 'הצלחה',
                            message: result.message || 'הפגישה שובצה בהצלחה',
                            variant: 'success'
                        }
                    });

                    // Close after success
                    if (onClose) {
                        setTimeout(() => onClose(), 1500);
                    }
                } else {
                    handlers.showToast({
                        detail: {
                            title: 'שגיאה',
                            message: result?.message || 'שגיאה בשיבוץ הפגישה',
                            variant: 'error'
                        }
                    });
                }
            } catch (e) {
                console.error('[DynamicLWC] Manager approval assignment error:', e);
                setComponentState(prev => ({ ...prev, isAssigning: false }));
                handlers.showToast({
                    detail: {
                        title: 'שגיאה',
                        message: e.message || 'שגיאה בשיבוץ הפגישה',
                        variant: 'error'
                    }
                });
            }
        };

        // Approval reason handlers
        handlers.handleApprovalReasonChange = (event) => {
            const reason = event?.target?.value || '';
            setComponentState(prev => ({
                ...prev,
                approvalReason: reason
            }));
        };

        handlers.submitApprovalRequest = async () => {
            console.log('[DynamicLWC] submitApprovalRequest');
            const currentState = stateRef.current;

            if (!currentState.approvalReason?.trim()) {
                handlers.showToast({
                    detail: {
                        title: 'שגיאה',
                        message: 'יש להזין סיבה לבקשה',
                        variant: 'warning'
                    }
                });
                return;
            }

            // Submit the approval request
            setComponentState(prev => ({
                ...prev,
                showApprovalReasonModal: false,
                approvalReason: ''
            }));

            handlers.showToast({
                detail: {
                    title: 'הצלחה',
                    message: 'בקשת האישור נשלחה בהצלחה',
                    variant: 'success'
                }
            });
        };

        // Close assign modal
        handlers.closeAssignModal = () => {
            setComponentState(prev => ({ ...prev, showAssignModal: false }));
        };

        // Cancel confirmation
        handlers.cancelConfirmation = () => {
            setComponentState(prev => ({
                ...prev,
                showConfirmModal: false,
                showAssignModal: true
            }));
        };

        // Time change handler
        handlers.handleTimeChange = (event) => {
            const time = event?.detail?.value || event?.target?.value;
            console.log('[DynamicLWC] handleTimeChange:', time);
            setComponentState(prev => ({
                ...prev,
                assignmentTime: time,
                selectedTimeForAssign: time
            }));
        };

        // Select recommended time
        handlers.selectRecommendedTime = (event) => {
            const time = event?.currentTarget?.dataset?.time;
            console.log('[DynamicLWC] selectRecommendedTime:', time);
            setComponentState(prev => ({
                ...prev,
                assignmentTime: time,
                selectedTimeForAssign: time
            }));
        };

        // Execute assignment (from assign modal)
        handlers.executeAssignment = async () => {
            console.log('[DynamicLWC] executeAssignment');
            const currentState = stateRef.current;

            // Show confirmation modal
            setComponentState(prev => ({
                ...prev,
                appointmentDetails: {
                    appointmentNumber: prev.appointmentNumber || prev.serviceAppointmentId,
                    technicianName: prev.selectedTechName,
                    territoryName: prev.selectedTerritoryName || prev.currentTerritoryName,
                    date: prev.selectedDate,
                    time: prev.assignmentTime || '09:00'
                },
                showAssignModal: false,
                showConfirmModal: true
            }));
        };

        // Final confirm assignment
        handlers.confirmAssignment = async () => {
            console.log('[DynamicLWC] confirmAssignment');
            const currentState = stateRef.current;

            try {
                setComponentState(prev => ({ ...prev, isAssigning: true }));

                const result = await callApex('TechnicianSelectorService', 'assignAppointmentToTechnicianWithReason', {
                    serviceAppointmentId: currentState.serviceAppointmentId || recordId,
                    technicianId: currentState.selectedTechId,
                    scheduledDate: currentState.selectedDate,
                    startTime: currentState.assignmentTime || '09:00',
                    reason: currentState.isManagerApproved ? `אישור מנהל: ${currentState.selectedManagerName}` : ''
                });

                console.log('[DynamicLWC] Assignment result:', result);

                setComponentState(prev => ({
                    ...prev,
                    showConfirmModal: false,
                    isAssigning: false
                }));

                handlers.showToast({
                    detail: {
                        title: 'הצלחה',
                        message: result?.message || 'הפגישה שובצה בהצלחה',
                        variant: 'success'
                    }
                });

                // Close after success
                if (onClose) {
                    setTimeout(() => onClose(), 1500);
                }
            } catch (e) {
                console.error('[DynamicLWC] Assignment error:', e);
                setComponentState(prev => ({ ...prev, isAssigning: false }));
                handlers.showToast({
                    detail: {
                        title: 'שגיאה',
                        message: e.message || 'שגיאה בשיבוץ הפגישה',
                        variant: 'error'
                    }
                });
            }
        };

        // Territory mismatch handlers
        handlers.continueDespiteMismatch = () => {
            console.log('[DynamicLWC] continueDespiteMismatch');
            setComponentState(prev => ({
                ...prev,
                showTerritoryMismatchModal: false,
                showAssignModal: true,
                isTerritoryMismatch: true
            }));
        };

        // Assign appointment
        handlers.handleConfirmAssign = async () => {
            console.log('[DynamicLWC] handleConfirmAssign');
            const currentState = stateRef.current;

            try {
                const result = await callApex('TechnicianSelectorService', 'assignAppointmentToTechnicianWithReason', {
                    serviceAppointmentId: currentState.serviceAppointmentId || recordId,
                    technicianId: currentState.selectedTechnicianForAssign?.id,
                    scheduledDate: currentState.selectedDateForAssign,
                    scheduledTime: currentState.selectedTimeForAssign || '09:00'
                });

                console.log('[DynamicLWC] Assignment result:', result);

                handlers.showToast({
                    detail: {
                        title: 'הצלחה',
                        message: result.message || 'הפגישה שובצה בהצלחה',
                        variant: 'success'
                    }
                });

                setComponentState(prev => ({ ...prev, showAssignModal: false, showConfirmModal: false }));

                // Optionally close the quick action
                if (onClose) {
                    setTimeout(() => onClose(), 1500);
                }
            } catch (e) {
                console.error('[DynamicLWC] Assignment error:', e);
                handlers.showToast({
                    detail: {
                        title: 'שגיאה',
                        message: e.message || 'שגיאה בשיבוץ הפגישה',
                        variant: 'error'
                    }
                });
            }
        };

        // Show reschedule view (for already scheduled appointments)
        handlers.showRescheduleView = () => {
            console.log('[DynamicLWC] showRescheduleView');
            setComponentState(prev => ({
                ...prev,
                isAlreadyScheduled: false,
                showFiltersSection: true,
                showEmptyState: true
            }));
        };

        return handlers;
    }, [onClose]); // Dependencies minimal - we use stateRef for current state access

    // Render loading state - simple text only
    if (loading) {
        return (
            <div className="lwc-loading" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                minHeight: '200px',
                color: '#706e6b',
                fontSize: '14px'
            }}>
                טוען...
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="lwc-error" style={{
                background: '#fef0f0',
                border: '1px solid #c23934',
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'center',
                margin: '16px'
            }}>
                <h2 style={{ color: '#c23934', margin: '0 0 8px 0', fontSize: '18px' }}>שגיאה בטעינת קומפוננטה</h2>
                <p style={{ color: '#706e6b', margin: 0 }}>{error}</p>
            </div>
        );
    }

    const handlers = createHandlers();

    return (
        <div
            ref={containerRef}
            className={`lwc-container lwc-${componentName} slds-scope`}
            dir="rtl"
            style={{ width: '100%', minHeight: '100px' }}
        >
            <link
                rel="stylesheet"
                href="/vendor/design-system/assets/styles/salesforce-lightning-design-system.min.css"
            />
            <style>{`
                @keyframes lwc-spin {
                    to { transform: rotate(360deg); }
                }
                .lwc-${componentName} { font-family: 'Salesforce Sans', 'Segoe UI', Arial, sans-serif; }
                .lwc-${componentName} .slds-form-element { margin-bottom: 16px; }
                .lwc-${componentName} .slds-grid { display: flex; flex-wrap: wrap; }
                .lwc-${componentName} .slds-col { padding: 0 12px; }
                .lwc-${componentName} .slds-size_1-of-2 { width: 50%; }
                .lwc-${componentName} .slds-size_1-of-3 { width: 33.33%; }
                .lwc-${componentName} .slds-size_1-of-4 { width: 25%; }
                .lwc-${componentName} .slds-size_1-of-1 { width: 100%; }
                .lwc-${componentName} .slds-card { margin-bottom: 16px; border: 1px solid #e5e5e5; border-radius: 8px; background: #fff; }
                .lwc-${componentName} .slds-card__header { padding: 12px 16px; border-bottom: 1px solid #e5e5e5; }
                .lwc-${componentName} .slds-card__body { padding: 16px; }
                .lwc-${componentName} .slds-card__header-title span { font-weight: 700; font-size: 16px; color: #16325c; }
                .lwc-${componentName} .slds-table { width: 100%; border-collapse: collapse; }
                .lwc-${componentName} .slds-table th, .lwc-${componentName} .slds-table td {
                    padding: 8px 12px;
                    border-bottom: 1px solid #e5e5e5;
                    text-align: right;
                }
                .lwc-${componentName} .slds-table th { background: #fafaf9; font-weight: 600; font-size: 12px; text-transform: uppercase; }
                .lwc-${componentName} .slds-button { cursor: pointer; padding: 8px 16px; border-radius: 4px; font-size: 14px; }
                .lwc-${componentName} .slds-button_brand { background: #0176d3; color: #fff; border: none; }
                .lwc-${componentName} .slds-button_brand:hover { background: #014486; }
                .lwc-${componentName} .slds-button_neutral { background: #fff; color: #0176d3; border: 1px solid #c9c9c9; }
                .lwc-${componentName} .slds-input, .lwc-${componentName} .slds-select, .lwc-${componentName} .slds-textarea {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #c9c9c9;
                    border-radius: 4px;
                    font-size: 14px;
                }
                .lwc-${componentName} .slds-input:focus, .lwc-${componentName} .slds-select:focus {
                    outline: none;
                    border-color: #0176d3;
                    box-shadow: 0 0 0 2px rgba(1,118,211,0.2);
                }
                .lwc-${componentName} .slds-form-element__label { display: block; margin-bottom: 4px; font-size: 13px; font-weight: 600; color: #3e3e3c; }
                .lwc-${componentName} .slds-m-bottom_small { margin-bottom: 12px; }
                .lwc-${componentName} .slds-m-bottom_medium { margin-bottom: 16px; }
                .lwc-${componentName} .slds-m-top_medium { margin-top: 16px; }
                .lwc-${componentName} .slds-p-around_medium { padding: 16px; }
                .lwc-${componentName} .slds-text-heading_small { font-size: 16px; font-weight: 700; }
                .lwc-${componentName} .slds-text-heading_medium { font-size: 20px; font-weight: 700; }
                .lwc-${componentName} .slds-badge { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; background: #ecebea; }
                .lwc-${componentName} .slds-spinner_container { min-height: 50px; position: relative; }
                .lwc-${componentName} .lwc-custom { padding: 8px; border: 1px dashed #c9c9c9; border-radius: 4px; margin: 8px 0; }

                /* Custom table styles - match legacy CRM LWC styling */
                .lwc-${componentName} .custom-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-family: 'Salesforce Sans', 'Segoe UI', Arial, sans-serif;
                    font-size: 14px;
                    margin-top: 10px;
                    background: #fff;
                }
                .lwc-${componentName} .custom-table th,
                .lwc-${componentName} .custom-table td {
                    padding: 10px 12px;
                    text-align: right;
                    border-bottom: 1px solid #dddbda;
                    color: #333;
                }
                .lwc-${componentName} .custom-table th {
                    background-color: #f3f2f2;
                    font-weight: bold;
                    white-space: nowrap;
                    border-bottom: 2px solid #c9c7c5;
                }
                .lwc-${componentName} .custom-table tbody tr:hover {
                    background-color: #f5f5f5;
                }
                .lwc-${componentName} .custom-table-row { transition: background-color 0.15s ease; }
                .lwc-${componentName} .custom-table a { color: #0176d3; text-decoration: none; font-weight: 500; }
                .lwc-${componentName} .custom-table a:hover { text-decoration: underline; }

                /* Drag handle - exact match to SF */
                .lwc-${componentName} .drag-handle {
                    cursor: move !important;
                    width: 40px !important;
                    min-width: 40px !important;
                    text-align: center !important;
                    color: #706e6b;
                }
                .lwc-${componentName} .drag-handle:hover { color: #0176d3; }
                .lwc-${componentName} .drag-source {
                    padding: 4px;
                    display: inline-block;
                    cursor: grab;
                }
                .lwc-${componentName} .drag-source:active { cursor: grabbing; }

                /* Dragging states */
                .lwc-${componentName} .custom-table-row.dragging {
                    background-color: #f0f8ff !important;
                    opacity: 0.8;
                }
                .lwc-${componentName} .custom-table-row.drag-over {
                    border-top: 2px solid #0176d3 !important;
                }

                /* Theme colors for validation */
                .lwc-${componentName} .slds-theme_error { background-color: #f4c6c9 !important; }
                .lwc-${componentName} .slds-theme_success { background-color: #a3d39c !important; }

                /* Text colors */
                .lwc-${componentName} .slds-text-color_weak { color: #706e6b; }
                .lwc-${componentName} .slds-text-color_error { color: #c23934; }
                .lwc-${componentName} .slds-text-body_small { font-size: 12px; }

                /* Box styling - match SF cards */
                .lwc-${componentName} .slds-box {
                    padding: 16px;
                    border: 1px solid #e5e5e5;
                    border-radius: 8px;
                    background: #fff;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }

                /* Button icon styles */
                .lwc-${componentName} .slds-button_icon {
                    padding: 6px 10px;
                    min-width: 34px;
                    border: 1px solid #c9c9c9;
                    border-radius: 4px;
                    background: #fff;
                    margin: 0 2px;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .lwc-${componentName} .slds-button_icon:hover {
                    background: #f4f6f9;
                    border-color: #0176d3;
                }

                /* Map container */
                .lwc-${componentName} .lwc-map-container {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    margin-bottom: 16px;
                }

                /* Grid layout helpers */
                .lwc-${componentName} .slds-grid { display: flex; flex-wrap: wrap; gap: 8px; }
                .lwc-${componentName} .slds-gutters_small > * { margin-right: 8px; }
                .lwc-${componentName} .slds-m-right_small { margin-right: 8px; }
                .lwc-${componentName} .slds-m-bottom_x-small { margin-bottom: 4px; }
                .lwc-${componentName} .slds-m-vertical_small { margin-top: 8px; margin-bottom: 8px; }

                /* Inner Modal Styles - for modals within the LWC template */
                .lwc-${componentName} .slds-modal {
                    position: fixed !important;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    z-index: 9001;
                    opacity: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .lwc-${componentName} .slds-modal__container {
                    position: relative;
                    margin: 0 auto;
                    width: 90%;
                    max-width: 500px;
                    min-width: 300px;
                    background: #fff;
                    border-radius: 8px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.25);
                    overflow: hidden;
                }
                .lwc-${componentName} .slds-modal__header {
                    padding: 12px 16px;
                    background: linear-gradient(135deg, #0176d3, #00a1e0);
                    color: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .lwc-${componentName} .slds-modal__title {
                    color: #fff;
                    font-size: 16px;
                    font-weight: 700;
                    margin: 0;
                }
                .lwc-${componentName} .slds-modal__close {
                    background: transparent;
                    border: none;
                    color: #fff;
                    cursor: pointer;
                    padding: 4px;
                    opacity: 0.9;
                }
                .lwc-${componentName} .slds-modal__close:hover {
                    opacity: 1;
                }
                .lwc-${componentName} .slds-modal__content {
                    padding: 16px;
                    background: #fff;
                    max-height: 60vh;
                    overflow-y: auto;
                }
                .lwc-${componentName} .slds-modal__footer {
                    padding: 12px 16px;
                    background: #f4f6f9;
                    border-top: 1px solid #e5e5e5;
                    display: flex;
                    justify-content: flex-end;
                    gap: 8px;
                }

                /* Modal Backdrop */
                .lwc-${componentName} .slds-backdrop {
                    position: fixed;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    left: 0;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 9000;
                }
                .lwc-${componentName} .slds-backdrop_open {
                    opacity: 1;
                }

                /* Assignment details in modal */
                .lwc-${componentName} .assignment-details {
                    background: #f8f9fa;
                    border-radius: 6px;
                    padding: 12px;
                    margin-bottom: 16px;
                }
                .lwc-${componentName} .detail-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px solid #e5e5e5;
                }
                .lwc-${componentName} .detail-row:last-child {
                    border-bottom: none;
                }
                .lwc-${componentName} .detail-label {
                    color: #706e6b;
                    font-weight: 600;
                }
                .lwc-${componentName} .detail-value {
                    color: #16325c;
                    font-weight: 700;
                }

                /* Time selector */
                .lwc-${componentName} .time-selector {
                    margin-top: 12px;
                }
                .lwc-${componentName} .time-input {
                    width: 100%;
                }
            `}</style>
            {componentData?.html ? (
                parseLWCTemplate(componentData.html, componentState, handlers, componentName)
            ) : (
                <div className="lwc-empty" style={{
                    textAlign: 'center',
                    padding: '40px',
                    background: '#f8f8f8',
                    borderRadius: '8px'
                }}>
                    <p style={{ color: '#706e6b', fontSize: '16px', marginBottom: '8px' }}>קומפוננטה: {componentName}</p>
                    <p style={{ color: '#969492', fontSize: '14px' }}>אין תוכן HTML להצגה</p>
                </div>
            )}
        </div>
    );
}

/**
 * LWC Modal Wrapper - Auto-expands to fit content
 * IMPORTANT: Modals should be large by default to fit component content
 */
export function ComponentModal({
    isOpen,
    onClose,
    componentName,
    title,
    width = 'full',  // Default to full width for LWC components
    height = 'auto',
    recordId,
    objectName,
    ...props
}) {
    if (!isOpen) return null;

    // Width map - generous sizes for LWC content
    const widthMap = {
        small: '700px',
        medium: '900px',
        large: '1200px',
        xlarge: '1500px',
        full: '95vw'
    };

    return (
        <div
            className="lwc-modal-overlay"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '16px',
                overflowY: 'auto'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="lwc-modal-container"
                style={{
                    width: widthMap[width] || width,
                    minWidth: '800px',
                    maxWidth: '98vw',
                    maxHeight: '95vh',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    marginTop: '16px',
                    marginBottom: '16px',
                    position: 'relative'
                }}
            >
                <header style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e5e5e5',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #0176d3, #00a1e0)',
                    color: '#fff',
                    flexShrink: 0
                }}>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#fff' }}>
                        {title || componentName}
                    </h2>
                    <button
                        onClick={onClose}
                        title="סגור"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            color: '#fff',
                            lineHeight: 1,
                            opacity: 0.9
                        }}
                    >
                        ×
                    </button>
                </header>
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '16px',
                    position: 'relative'
                }}>
                    <DynamicComponentRenderer
                        componentName={componentName}
                        recordId={recordId}
                        objectName={objectName}
                        onClose={onClose}
                        {...props}
                    />
                </div>
            </div>
        </div>
    );
}

export default DynamicComponentRenderer;
